import React, { useState, useEffect } from 'react';
import { ProfessionalReportsModule } from './ProfessionalReportsModule';
import { Sidebar } from './components/Sidebar';
import { TopBar } from './components/TopBar';
import { Login } from './components/Login';
import { ChangePassword } from './components/ChangePassword';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import './design-system.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';


function AppContent() {
  const { user, logout, token } = useAuth();

  // Helper function for authenticated API calls
  const authFetch = async (endpoint: string, options: RequestInit = {}): Promise<Response> => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> || {})
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return fetch(endpoint, {
      ...options,
      headers
    });
  };

  // Main module state
  const [activeModule, setActiveModule] = useState<'dashboard' | 'historico' | 'protection' | 'monitoring' | 'reports'>('dashboard');

  // Protection Manager state
  const [destination, setDestination] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState('');
  const [flowspecTab, setFlowspecTab] = useState<'create' | 'manage'>('create');

  // Rules management state (RESTORED)
  const [rules, setRules] = useState<any[]>([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [selectedRules, setSelectedRules] = useState<Set<string>>(new Set());
  const [batchLoading, setBatchLoading] = useState(false);
  const [rulesFilter, setRulesFilter] = useState<string>('');

  // Monitoring and attack data
  const [attackData, setAttackData] = useState<any>(null);
  const [monitoringData, setMonitoringData] = useState<any>(null);
  const [monitoringLoading, setMonitoringLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<string>('');

  // Modals state (RESTORED)
  const [showingDetails, setShowingDetails] = useState(false);
  const [attackDetails, setAttackDetails] = useState<any>(null);
  const [showingReport, setShowingReport] = useState(false);
  const [intelligenceReport, setIntelligenceReport] = useState<any>(null);

  // Kentik Analysis Modal state
  const [showingKentikModal, setShowingKentikModal] = useState(false);
  const [kentikAnalysis, setKentikAnalysis] = useState<any>(null);
  const [kentikLoading, setKentikLoading] = useState(false);
  const [kentikAttackInfo, setKentikAttackInfo] = useState<any>(null);
  const [kentikAIAnalysis, setKentikAIAnalysis] = useState<any>(null);
  const [kentikAILoading, setKentikAILoading] = useState(false);

  // Protection Manager advanced state (RESTORED)
  const [aiRecommendations, setAiRecommendations] = useState<any>(null);
  const [selectedTemplates, setSelectedTemplates] = useState<Set<string>>(new Set());
  const [previewData, setPreviewData] = useState<any>(null);
  const [showingPreview, setShowingPreview] = useState(false);

  // Reports module state (RESTORED)
  const [reportsData, setReportsData] = useState<any>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportStep, setReportStep] = useState<'data' | 'audience' | 'format'>('data');
  const [selectedDataType, setSelectedDataType] = useState<string>('');
  const [selectedAudience, setSelectedAudience] = useState<string>('');
  const [selectedFormat, setSelectedFormat] = useState<string>('dashboard');
  const [selectedPeriod, setSelectedPeriod] = useState<string>('24h');
  const [activeReport, setActiveReport] = useState<'executive' | 'anatel' | 'intelligence' | 'forensics' | 'prospects'>('executive');

  // Attack History state
  const [attackHistory, setAttackHistory] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPagination, setHistoryPagination] = useState<any>({ total: 0, limit: 20, offset: 0, hasMore: false });
  const [historyPeriod, setHistoryPeriod] = useState<string>('7d');

  // Config Modal state
  const [showingConfigModal, setShowingConfigModal] = useState(false);
  const [selectedAttackConfigs, setSelectedAttackConfigs] = useState<any>(null);
  const [configModalTab, setConfigModalTab] = useState<'flowspec' | 'bpf' | 'infrastructure'>('flowspec');

  // Change Password Modal state
  const [showChangePassword, setShowChangePassword] = useState(false);

  // Fetch rules when switching to manage tab
  useEffect(() => {
    if (activeModule === 'protection' && flowspecTab === 'manage') {
      fetchRules();
    }
  }, [activeModule, flowspecTab]);

  // Fetch monitoring data when switching to monitoring or Dashboard tab
  useEffect(() => {
    if (activeModule === 'monitoring' || activeModule === 'dashboard') {
      fetchMonitoringData();
      const refreshInterval = activeModule === 'dashboard' ? 5000 : 15000;
      const interval = setInterval(fetchMonitoringData, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [activeModule]);

  // ==================== DATA FETCHING FUNCTIONS ====================

  const fetchRules = async () => {
    setRulesLoading(true);
    try {
      const response = await authFetch(API_BASE + '/flowspec/rules');
      const data = await response.json();
      if (response.ok) {
        setRules(data.items || []);
      } else {
        setError('Erro ao carregar regras');
      }
    } catch (err) {
      setError('Erro de conexão ao carregar regras');
    } finally {
      setRulesLoading(false);
    }
  };

  const fetchMonitoringData = async () => {
    setMonitoringLoading(true);
    try {
      const [dashboardResponse, attacksResponse] = await Promise.all([
        authFetch(API_BASE + '/monitoring/dashboard'),
        authFetch(API_BASE + '/monitoring/attacks')
      ]);

      if (dashboardResponse.ok) {
        const dashboardData = await dashboardResponse.json();
        setMonitoringData(dashboardData.data);
      }

      if (attacksResponse.ok) {
        const attacksData = await attacksResponse.json();
        setAttackData(attacksData.data);
      }

      setLastUpdate(new Date().toLocaleTimeString());
      setError('');
    } catch (err) {
      setError('Erro de conexão ao carregar dados de monitoramento');
    } finally {
      setMonitoringLoading(false);
    }
  };

  const fetchIntelligenceReport = async () => {
    try {
      console.log('🤖 Fetching AI-powered intelligence report...');
      const response = await authFetch(API_BASE + '/ai/threat-intelligence', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ period: '24h' })
      });
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Received AI intelligence report:', data);
        setIntelligenceReport(data);
        setShowingReport(true);
      } else {
        setError('Erro ao gerar relatório de inteligência');
      }
    } catch (err) {
      setError('Erro de conexão ao gerar relatório');
    }
  };

  const fetchAttackDetails = async (attackId: string) => {
    try {
      const response = await authFetch(`${API_BASE}/monitoring/attack-details/${attackId}`);
      if (response.ok) {
        const data = await response.json();
        setAttackDetails(data.attackDetails);
        setShowingDetails(true);
      } else {
        setError('Erro ao buscar detalhes do ataque');
      }
    } catch (err) {
      setError('Erro de conexão ao buscar detalhes');
    }
  };

  const fetchForensicAnalysis = async (attackId: string) => {
    try {
      console.log('🔬 Fetching AI forensic analysis for attack:', attackId);
      const response = await authFetch(`${API_BASE}/ai/attack-forensic-analysis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attackId })
      });
      if (response.ok) {
        const data = await response.json();
        console.log('✅ AI Forensic analysis received:', data);
        setAttackDetails({
          ...data.analysis,
          isForensicAnalysis: true,
          attackId: data.attackId,
          aiGenerated: data.analysis.aiGenerated || false
        });
        setShowingDetails(true);
      } else {
        setError('Erro ao buscar análise forense');
      }
    } catch (err) {
      setError('Erro de conexão ao buscar análise forense');
    }
  };

  const queryKentikData = async (attack: any) => {
    // Open modal immediately with loading state
    setShowingKentikModal(true);
    setKentikLoading(true);
    setKentikAnalysis(null);

    try {
      console.log('📊 Querying Kentik for attack data:', {
        attackId: attack.id,
        targetIP: attack.ipAddress
      });

      // Calculate EXACT time range of the attack (no buffer to avoid unrelated traffic)
      const startTime = new Date(attack.startTimeEpoch * 1000).toISOString();
      const endTime = new Date((attack.startTimeEpoch + attack.durationSeconds) * 1000).toISOString();

      // Store attack info for modal display
      setKentikAttackInfo({
        attackId: attack.id,
        targetIP: attack.ipAddress,
        startTime: attack.startTime,
        duration: attack.duration,
        peakVolume: attack.peakVolume,
        protocol: attack.protocol
      });

      // Extract protocol from attack description or use attack.protocol
      let attackProtocol = attack.protocol;
      if (!attackProtocol && attack.description) {
        // Try to extract protocol from description (e.g., "UDP Flood", "SYN Flood")
        if (attack.description.toLowerCase().includes('udp')) attackProtocol = 'UDP';
        else if (attack.description.toLowerCase().includes('tcp') || attack.description.toLowerCase().includes('syn')) attackProtocol = 'TCP';
        else if (attack.description.toLowerCase().includes('icmp')) attackProtocol = 'ICMP';
      }

      console.log('🔍 Kentik filters:', {
        targetIP: attack.ipAddress,
        protocol: attackProtocol || 'ALL',
        exactTimeRange: `${startTime} → ${endTime}`
      });

      // Call backend Kentik endpoint (4 parallel queries with protocol filter)
      const response = await authFetch(`${API_BASE}/kentik/query-attack`, {
        method: 'POST',
        body: JSON.stringify({
          ipAddress: attack.ipAddress,
          startTime,
          endTime,
          protocol: attackProtocol || null
        })
      });

      const data = await response.json();

      if (data.success) {
        console.log('✅ Kentik query successful:', {
          sourceIPs: data.analysis?.sourceIPs?.results?.[0]?.data?.length || 0,
          sourcePorts: data.analysis?.sourcePorts?.results?.[0]?.data?.length || 0,
          destPorts: data.analysis?.destPorts?.results?.[0]?.data?.length || 0,
          protocols: data.analysis?.protocols?.results?.[0]?.data?.length || 0,
          packetSizes: data.analysis?.packetSizes?.results?.[0]?.data?.length || 0,
          tcpFlags: data.analysis?.tcpFlags?.results?.[0]?.data?.length || 0
        });

        setKentikAnalysis(data.analysis);
        setError('');
      } else {
        throw new Error(data.error || 'Failed to query Kentik');
      }

    } catch (err: any) {
      console.error('❌ Error querying Kentik:', err);
      setError('Erro ao consultar Kentik: ' + err.message);
      setShowingKentikModal(false); // Close modal on error
      alert('❌ Erro ao consultar dados do Kentik. Verifique os logs.');
    } finally {
      setKentikLoading(false);
    }
  };

  const analyzeWithAI = async () => {
    if (!kentikAnalysis || !kentikAttackInfo) {
      alert('❌ Dados do Kentik não disponíveis para análise');
      return;
    }

    setKentikAILoading(true);
    try {
      console.log('🤖 Analyzing Kentik data with Gemini AI...');

      // Reduce payload size - send only top 10 of each dimension to avoid 413 error
      const reducedKentikData = {
        sourceIPs: {
          results: [{
            data: kentikAnalysis.sourceIPs?.results?.[0]?.data?.slice(0, 10) || []
          }]
        },
        sourcePorts: {
          results: [{
            data: kentikAnalysis.sourcePorts?.results?.[0]?.data?.slice(0, 10) || []
          }]
        },
        destPorts: {
          results: [{
            data: kentikAnalysis.destPorts?.results?.[0]?.data?.slice(0, 10) || []
          }]
        },
        protocols: {
          results: [{
            data: kentikAnalysis.protocols?.results?.[0]?.data || []
          }]
        },
        packetSizes: {
          results: [{
            data: kentikAnalysis.packetSizes?.results?.[0]?.data?.slice(0, 10) || []
          }]
        },
        tcpFlags: {
          results: [{
            data: kentikAnalysis.tcpFlags?.results?.[0]?.data?.slice(0, 10) || []
          }]
        }
      };

      const response = await authFetch(`${API_BASE}/ai/kentik-analysis`, {
        method: 'POST',
        body: JSON.stringify({
          kentikData: reducedKentikData,
          attackInfo: kentikAttackInfo
        })
      });

      const data = await response.json();

      if (data.success) {
        console.log('✅ AI analysis completed:', {
          attackType: data.analysis?.attackIdentification?.attackType,
          confidence: data.analysis?.attackIdentification?.confidence,
          aiGenerated: data.analysis?.aiGenerated
        });

        setKentikAIAnalysis(data.analysis);
        setError('');
      } else {
        throw new Error(data.error || 'Failed to analyze with AI');
      }

    } catch (err: any) {
      console.error('❌ Error in AI analysis:', err);
      setError('Erro ao analisar com IA: ' + err.message);
      alert('❌ Erro ao gerar análise de IA. Tente novamente.');
    } finally {
      setKentikAILoading(false);
    }
  };

  const fetchAttackHistory = async (period: string = '7d', offset: number = 0) => {
    setHistoryLoading(true);
    try {
      console.log(`📜 Fetching attack history (period: ${period}, offset: ${offset})...`);
      const response = await authFetch(`${API_BASE}/monitoring/attack-history?period=${period}&limit=20&offset=${offset}`);
      if (response.ok) {
        const data = await response.json();
        console.log(`✅ Received ${data.data.length} attacks from history`);

        // If offset is 0, replace all data (new period selected)
        // If offset > 0, append to existing data (load more)
        if (offset === 0) {
          setAttackHistory(data.data);
        } else {
          setAttackHistory(prev => [...prev, ...data.data]);
        }

        setHistoryPagination(data.pagination);
        setHistoryPeriod(period);
        setError('');
      } else {
        setError('Erro ao buscar histórico de ataques');
      }
    } catch (err) {
      setError('Erro de conexão ao buscar histórico');
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchAIConfigs = async (attack: any) => {
    try {
      console.log('🤖 Generating AI configs for attack:', attack.attackId || attack.id);
      setLoading(true);

      const response = await authFetch(`${API_BASE}/ai/attack-forensic-analysis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attackId: attack.attackId || attack.id,
          attackData: attack.rawData || attack
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ AI configs generated:', data);

        setSelectedAttackConfigs({
          attack: attack,
          analysis: data.analysis,
          flowspecRules: data.analysis.mitigationRecommendations?.flowspecRules || [],
          bpfFilters: data.analysis.mitigationRecommendations?.bpfFilters || [],
          infrastructureActions: data.analysis.mitigationRecommendations?.infrastructureActions || [],
          aiGenerated: data.analysis.aiGenerated || false
        });
        setShowingConfigModal(true);
        setConfigModalTab('flowspec');
        setError('');
      } else {
        setError('Erro ao gerar configurações IA');
      }
    } catch (err) {
      setError('Erro de conexão ao gerar configurações');
    } finally {
      setLoading(false);
    }
  };

  const getEnhancedAIRecommendations = async (targetDestination: string) => {
    try {
      const response = await authFetch(API_BASE + '/protection/analyze-and-recommend-enhanced', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          destination: targetDestination
        }),
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        console.error('Failed to get enhanced AI recommendations');
        return null;
      }
    } catch (err) {
      console.error('Error getting enhanced AI recommendations:', err);
      return null;
    }
  };

  // ==================== PROTECTION MANAGER FUNCTIONS ====================

  const getAIRecommendations = async (targetDestination: string) => {
    try {
      const response = await authFetch(API_BASE + '/protection/analyze-and-recommend', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          destination: targetDestination
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setAiRecommendations(data);
        const criticalKeys = data.recommendations.critical.map((t: any) => t.key);
        setSelectedTemplates(new Set(criticalKeys));
        return data;
      } else {
        console.error('Failed to get AI recommendations');
        return null;
      }
    } catch (err) {
      console.error('Error getting AI recommendations:', err);
      return null;
    }
  };

  const generatePreview = async () => {
    if (!destination.trim()) {
      setError('Por favor, insira um destino');
      return;
    }

    try {
      const selectedTemplatesArray = Array.from(selectedTemplates);
      const response = await authFetch(API_BASE + '/protection/preview', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          destination: destination.trim(),
          selectedTemplates: selectedTemplatesArray.length > 0 ? selectedTemplatesArray : undefined
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setPreviewData(data);
        setShowingPreview(true);
        setError('');
      } else {
        const errorData = await response.json();
        setError('Erro ao gerar preview: ' + (errorData.error || 'Erro desconhecido'));
      }
    } catch (err) {
      setError('Erro de conexão ao gerar preview');
    }
  };

  const createRules = async () => {
    if (!destination.trim()) {
      setError('Por favor, insira um destino');
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);

    try {
      const response = await authFetch(API_BASE + '/flowspec/create-protection', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          destination: destination.trim(),
          routeTemplate: 'Redirect to Local Scrubber'
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setResult(data);
        if (flowspecTab === 'manage') {
          await fetchRules();
        }
      } else {
        setError(data.error || 'Erro ao criar regras');
      }
    } catch (err) {
      setError('Erro de conexão com o servidor');
    } finally {
      setLoading(false);
    }
  };

  // ==================== BATCH OPERATIONS (RESTORED) ====================

  const removeBatchRules = async () => {
    const selectedArray = Array.from(selectedRules);
    if (selectedArray.length === 0) {
      alert('Selecione pelo menos uma regra para remover');
      return;
    }

    if (!window.confirm(`Tem certeza que deseja remover ${selectedArray.length} regra(s) selecionada(s)?`)) {
      return;
    }

    setBatchLoading(true);
    try {
      const response = await authFetch(API_BASE + '/flowspec/rules/batch-remove', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ruleNames: selectedArray
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSelectedRules(new Set());
        await fetchRules();
        alert(`Remoção em lote concluída: ${data.rulesRemoved} removidas, ${data.rulesFailed} falharam`);
      } else {
        alert('Erro na remoção em lote: ' + (data.error || 'Erro desconhecido'));
      }
    } catch (err) {
      alert('Erro de conexão na remoção em lote');
    } finally {
      setBatchLoading(false);
    }
  };

  const toggleRuleSelection = (ruleName: string) => {
    const newSelection = new Set(selectedRules);
    if (newSelection.has(ruleName)) {
      newSelection.delete(ruleName);
    } else {
      newSelection.add(ruleName);
    }
    setSelectedRules(newSelection);
  };

  const toggleSelectAll = () => {
    const filteredRuleNames = filteredRules.map(rule => rule.name);
    if (selectedRules.size === filteredRules.length && filteredRules.length > 0) {
      const newSelection = new Set(selectedRules);
      filteredRuleNames.forEach(name => newSelection.delete(name));
      setSelectedRules(newSelection);
    } else {
      const newSelection = new Set(selectedRules);
      filteredRuleNames.forEach(name => newSelection.add(name));
      setSelectedRules(newSelection);
    }
  };

  // Filter rules based on search term
  const filteredRules = rules.filter(rule => {
    if (!rulesFilter.trim()) return true;
    const searchTerm = rulesFilter.toLowerCase();
    const ruleName = rule.name?.toLowerCase() || '';
    const ruleMatch = rule.match?.toLowerCase() || '';
    const ruleDescription = rule.description?.toLowerCase() || '';
    return ruleName.includes(searchTerm) || ruleMatch.includes(searchTerm) || ruleDescription.includes(searchTerm);
  });

  // ==================== REPORTS FUNCTIONS (RESTORED) ====================

  const generateUnifiedReport = async () => {
    if (!selectedDataType || !selectedAudience) {
      setError('Selecione o tipo de dados e audiência');
      return;
    }

    setReportLoading(true);
    try {
      const response = await authFetch(API_BASE + '/reports/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          dataType: selectedDataType,
          audience: selectedAudience,
          format: selectedFormat,
          period: selectedPeriod
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setReportsData(data);
        setError('');
      } else {
        const errorData = await response.json();
        setError('Erro ao gerar relatório: ' + (errorData.error || 'Erro desconhecido'));
      }
    } catch (err) {
      setError('Erro de conexão ao gerar relatório');
    } finally {
      setReportLoading(false);
    }
  };

  // ==================== HELPER FUNCTIONS ====================

  const formatGbps = (bps: number) => {
    if (!bps || bps === 0) return '0 bps';
    const mbps = bps / 1000000;
    const gbps = mbps / 1000;
    return gbps >= 1 ? `${gbps.toFixed(2)} Gbps` : `${mbps.toFixed(2)} Mbps`;
  };

  const formatNumber = (n: number) => {
    return n?.toLocaleString('pt-BR') || '0';
  };

  const formatPercent = (value: number, total: number) => {
    if (!total || total === 0) return '0%';
    return `${((value / total) * 100).toFixed(1)}%`;
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--color-bg-primary)',
      fontFamily: 'var(--font-primary)'
    }}>
      {/* Sidebar Navigation */}
      <Sidebar
        currentModule={activeModule}
        onModuleChange={(module) => setActiveModule(module as any)}
      />

      {/* Top Bar with Persistent Metrics */}
      <TopBar
        activeAttacks={attackData?.ongoing?.length || 0}
        activeRules={monitoringData?.flowspecStats?.activeRules || 0}
        protectedPrefixes={monitoringData?.systemHealth?.assignedAssetCount || 0}
        avgMitigationTime={attackData?.ongoing?.[0]?.duration ? `${attackData.ongoing[0].duration}s` : '0s'}
        onChangePasswordClick={() => setShowChangePassword(true)}
      />

      {/* Change Password Modal */}
      {showChangePassword && (
        <ChangePassword onClose={() => setShowChangePassword(false)} />
      )}

      {/* Main Content Area */}
      <main style={{
        marginLeft: '240px',
        marginTop: '60px',
        padding: 'var(--spacing-6)',
        minHeight: 'calc(100vh - 60px)'
      }}>

        {/* ==================== SOC DASHBOARD - REDESIGNED ==================== */}
        {activeModule === 'dashboard' && (
          <div>
            {/* Dashboard Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 'var(--spacing-8)',
              flexWrap: 'wrap',
              gap: 'var(--spacing-4)'
            }}>
              <div>
                <h2 style={{
                  fontSize: 'var(--text-2xl)',
                  fontWeight: 'var(--font-bold)',
                  color: 'var(--color-text-primary)',
                  marginBottom: 'var(--spacing-2)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-3)'
                }}>
                  🚨 Centro de Operações de Segurança
                </h2>
                <p style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--color-text-tertiary)',
                  fontWeight: 'var(--font-medium)'
                }}>
                  Monitoramento em tempo real de ataques DDoS
                </p>
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-3)',
                background: 'var(--color-bg-elevated)',
                padding: 'var(--spacing-3) var(--spacing-4)',
                borderRadius: 'var(--radius-lg)',
                border: '1px solid var(--color-border)'
              }}>
                <div style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  background: 'var(--color-success)',
                  boxShadow: '0 0 8px var(--color-success)',
                  animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                }} />
                <span style={{
                  fontSize: 'var(--text-xs)',
                  color: 'var(--color-text-secondary)',
                  fontWeight: 'var(--font-medium)'
                }}>
                  Atualiza a cada 5s • {lastUpdate}
                </span>
              </div>
            </div>

            {/* Active Incidents Panel - Main Focus */}
            {attackData?.ongoing && attackData.ongoing.length > 0 && (
              <div style={{
                background: 'var(--gradient-card)',
                border: '1px solid var(--color-border-light)',
                borderRadius: 'var(--radius-xl)',
                padding: 'var(--spacing-6)',
                marginBottom: 'var(--spacing-8)',
                boxShadow: 'var(--shadow-lg)',
                borderLeft: '4px solid var(--color-critical)'
              }}>
                {/* Panel Header */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 'var(--spacing-6)',
                  paddingBottom: 'var(--spacing-4)',
                  borderBottom: '1px solid var(--color-border)'
                }}>
                  <h3 style={{
                    fontSize: 'var(--text-xl)',
                    fontWeight: 'var(--font-bold)',
                    color: 'var(--color-text-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-3)'
                  }}>
                    🚨 Ataques Ativos
                  </h3>
                  <span className="badge badge-critical" style={{
                    fontSize: 'var(--text-sm)',
                    padding: 'var(--spacing-2) var(--spacing-4)',
                    animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                  }}>
                    {attackData.ongoing.length} CRÍTICO{attackData.ongoing.length > 1 ? 'S' : ''}
                  </span>
                </div>

                {/* Attacks Table - Modern Design */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>
                  {attackData.ongoing.map((attack: any, index: number) => (
                    <div key={attack.id || index} className="card" style={{
                      background: 'var(--color-bg-elevated)',
                      border: '1px solid var(--color-border)',
                      borderLeft: '3px solid var(--color-critical)',
                      padding: 'var(--spacing-5)',
                      transition: 'all var(--transition-base)'
                    }}>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'auto 1fr auto',
                        gap: 'var(--spacing-5)',
                        alignItems: 'flex-start'
                      }}>
                        {/* Attack Icon */}
                        <div style={{
                          width: '64px',
                          height: '64px',
                          background: 'linear-gradient(135deg, var(--color-critical), var(--color-critical-dark))',
                          borderRadius: 'var(--radius-xl)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 'var(--text-3xl)',
                          boxShadow: 'var(--shadow-glow-critical)'
                        }}>
                          🎯
                        </div>

                        {/* Attack Details */}
                        <div style={{ flex: 1 }}>
                          {/* IP Address and Severity Badge */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--spacing-3)',
                            marginBottom: 'var(--spacing-3)'
                          }}>
                            <div style={{
                              fontSize: 'var(--text-lg)',
                              fontWeight: 'var(--font-bold)',
                              color: 'var(--color-critical-light)',
                              fontFamily: 'var(--font-mono)',
                              textShadow: '0 0 10px rgba(220, 38, 38, 0.3)'
                            }}>
                              {attack.ipAddress}
                            </div>
                            <span className="badge badge-critical">
                              HIGH
                            </span>
                            <span className="badge badge-outline badge-critical">
                              {attack.protocol || 'Unknown'}
                            </span>
                          </div>

                          {/* Attack Description */}
                          <div style={{
                            fontSize: 'var(--text-base)',
                            color: 'var(--color-text-primary)',
                            fontWeight: 'var(--font-semibold)',
                            marginBottom: 'var(--spacing-2)'
                          }}>
                            {attack.description}
                          </div>

                          {/* Metrics Grid */}
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                            gap: 'var(--spacing-3)',
                            marginBottom: 'var(--spacing-3)'
                          }}>
                            <div>
                              <span style={{
                                fontSize: 'var(--text-xs)',
                                color: 'var(--color-text-tertiary)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                fontWeight: 'var(--font-medium)'
                              }}>
                                Peak
                              </span>
                              <div style={{
                                fontSize: 'var(--text-base)',
                                fontWeight: 'var(--font-bold)',
                                color: 'var(--color-critical-light)'
                              }}>
                                {attack.peakVolume}
                              </div>
                            </div>
                            <div>
                              <span style={{
                                fontSize: 'var(--text-xs)',
                                color: 'var(--color-text-tertiary)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                fontWeight: 'var(--font-medium)'
                              }}>
                                Volume
                              </span>
                              <div style={{
                                fontSize: 'var(--text-base)',
                                fontWeight: 'var(--font-bold)',
                                color: 'var(--color-text-primary)'
                              }}>
                                {attack.totalVolume}
                              </div>
                            </div>
                            <div>
                              <span style={{
                                fontSize: 'var(--text-xs)',
                                color: 'var(--color-text-tertiary)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                fontWeight: 'var(--font-medium)'
                              }}>
                                Duração
                              </span>
                              <div style={{
                                fontSize: 'var(--text-base)',
                                fontWeight: 'var(--font-bold)',
                                color: 'var(--color-text-primary)'
                              }}>
                                {attack.duration}
                              </div>
                            </div>
                          </div>

                          {/* Additional Info */}
                          <div style={{
                            fontSize: 'var(--text-xs)',
                            color: 'var(--color-text-muted)',
                            display: 'flex',
                            gap: 'var(--spacing-4)',
                            flexWrap: 'wrap'
                          }}>
                            <span>🏢 {attack.tenantName}</span>
                            <span>📡 {attack.assetName}</span>
                            <span>🕒 {attack.startTime}</span>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 'var(--spacing-2)',
                          minWidth: '180px'
                        }}>
                          <button
                            className="btn btn-critical btn-sm"
                            onClick={() => {
                              setDestination(attack.ipAddress);
                              setActiveModule('protection');
                              setFlowspecTab('create');
                            }}
                          >
                            🛡️ Criar Proteção
                          </button>
                          <button
                            className="btn btn-critical btn-sm"
                            onClick={() => fetchForensicAnalysis(attack.id)}
                          >
                            🔬 Análise Forense
                          </button>
                          <button
                            className="btn btn-sm"
                            onClick={() => fetchAttackDetails(attack.id)}
                            style={{
                              background: 'var(--color-warning)',
                              color: 'white'
                            }}
                          >
                            🔍 Investigar
                          </button>
                          <button
                            className="btn btn-sm"
                            onClick={async () => {
                              const enhanced = await getEnhancedAIRecommendations(attack.ipAddress);
                              if (enhanced?.enhancedAnalysis?.specificRules?.length > 0) {
                                alert(`🧠 IA FORENSE ENHANCED\n\nBotnet: ${enhanced.enhancedAnalysis.forensicInsights?.botnetIdentification?.primaryBotnet}\nCampanha: ${enhanced.enhancedAnalysis.campaignCorrelation?.detectedCampaign}\nRegras: ${enhanced.enhancedAnalysis.specificRules.length} específicas\n\nClique "Análise Forense" para detalhes completos.`);
                              }
                            }}
                            style={{
                              background: 'var(--color-accent)',
                              color: 'white'
                            }}
                          >
                            🧠 IA Enhanced
                          </button>
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => fetchAIConfigs(attack)}
                            style={{
                              background: 'var(--gradient-hero)',
                              border: 'none',
                              boxShadow: 'var(--shadow-glow)'
                            }}
                          >
                            🤖 Gerar Configs
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* SOC Metrics Grid (RESTORED) */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '1rem',
              marginBottom: '2rem'
            }}>
              {/* Operational Metrics */}
              <div style={{
                background: 'var(--color-bg-card)',
                border: '1px solid var(--color-border)',
                borderRadius: '8px',
                padding: '1.5rem',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
              }}>
                <h4 style={{
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  color: 'var(--color-text-primary)',
                  marginBottom: '1rem',
                  borderBottom: '2px solid var(--color-border)',
                  paddingBottom: '0.5rem'
                }}>
                  📊 MÉTRICAS OPERACIONAIS
                </h4>
                <div style={{ fontSize: '0.875rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                    <span>Tempo Médio de Resposta:</span>
                    <span style={{ fontFamily: 'monospace', fontWeight: 'bold', color: 'var(--color-success)' }}>{'< 30s'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                    <span>Taxa de Detecção:</span>
                    <span style={{ fontFamily: 'monospace', fontWeight: 'bold', color: 'var(--color-success)' }}>100%</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                    <span>Falsos Positivos:</span>
                    <span style={{ fontFamily: 'monospace', fontWeight: 'bold', color: 'var(--color-success)' }}>{'< 1%'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Efetividade de Regras:</span>
                    <span style={{ fontFamily: 'monospace', fontWeight: 'bold', color: 'var(--color-success)' }}>98.5%</span>
                  </div>
                </div>
              </div>

              {/* Threat Intelligence (RESTORED) */}
              <div style={{
                background: 'var(--color-bg-card)',
                border: '1px solid var(--color-border)',
                borderRadius: '8px',
                padding: '1.5rem',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
              }}>
                <h4 style={{
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  color: 'var(--color-text-primary)',
                  marginBottom: '1rem',
                  borderBottom: '2px solid var(--color-border)',
                  paddingBottom: '0.5rem'
                }}>
                  🔍 THREAT INTELLIGENCE
                </h4>
                <div style={{ fontSize: '0.875rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                    <span>Ataques (24h):</span>
                    <span style={{ fontFamily: 'monospace', fontWeight: 'bold', color: 'var(--color-critical)' }}>
                      {intelligenceReport?.analysis?.totalAttacks || '591+'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                    <span>Tipo Dominante:</span>
                    <span style={{ fontFamily: 'monospace', fontWeight: 'bold', color: 'var(--color-critical)' }}>
                      {intelligenceReport?.dominantType?.replace('TCP ', '') || 'SYN/ACK Flood'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                    <span>Peak Máximo:</span>
                    <span style={{ fontFamily: 'monospace', fontWeight: 'bold', color: 'var(--color-warning)' }}>
                      {intelligenceReport?.analysis?.maxPeak ? `${(intelligenceReport.analysis.maxPeak / 1000).toFixed(1)} Gbps` : '12.8 Gbps'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Hora Pico:</span>
                    <span style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                      {intelligenceReport?.analysis?.peakHour ? `${intelligenceReport.analysis.peakHour}:00h` : '21:00h'}
                    </span>
                  </div>
                </div>

                {/* Intelligence Report Button (RESTORED) */}
                <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--color-border)' }}>
                  <button
                    onClick={fetchIntelligenceReport}
                    style={{
                      background: '#8b5cf6',
                      color: 'white',
                      border: 'none',
                      padding: '0.5rem 1rem',
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      width: '100%'
                    }}
                  >
                    🧠 Relatório Completo IA
                  </button>
                </div>
              </div>

              {/* Coverage & Protection (RESTORED) */}
              <div style={{
                background: 'var(--color-bg-card)',
                border: '1px solid var(--color-border)',
                borderRadius: '8px',
                padding: '1.5rem',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
              }}>
                <h4 style={{
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  color: 'var(--color-text-primary)',
                  marginBottom: '1rem',
                  borderBottom: '2px solid var(--color-border)',
                  paddingBottom: '0.5rem'
                }}>
                  🛡️ PROTEÇÃO & COBERTURA
                </h4>
                <div style={{ fontSize: '0.875rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                    <span>Prefixos Protegidos:</span>
                    <span style={{ fontFamily: 'monospace', fontWeight: 'bold', color: 'var(--color-success)' }}>
                      {monitoringData?.systemHealth?.assignedAssetCount || 0}/128 (100%)
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                    <span>Regras Protection:</span>
                    <span style={{ fontFamily: 'monospace', fontWeight: 'bold', color: 'var(--color-primary)' }}>
                      {monitoringData?.flowspecStats?.activeRules || 0} ativas
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                    <span>Grupos Protegidos:</span>
                    <span style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                      {monitoringData?.systemHealth?.assetGroupCount || 0}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Tenants Ativos:</span>
                    <span style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                      {(monitoringData?.systemHealth?.tenantCount?.SUBSCRIBED || 0)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Incident Timeline & Quick Actions (RESTORED) */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '2fr 1fr',
              gap: '2rem',
              marginBottom: '2rem'
            }}>
              {/* Incident Timeline */}
              <div style={{
                background: 'var(--color-bg-card)',
                border: '1px solid var(--color-border)',
                borderRadius: '8px',
                padding: '1.5rem',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
              }}>
                <h4 style={{
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  color: 'var(--color-text-primary)',
                  marginBottom: '1rem',
                  borderBottom: '2px solid var(--color-border)',
                  paddingBottom: '0.5rem'
                }}>
                  📅 TIMELINE DE INCIDENTES (Últimas 24h)
                </h4>

                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {/* Recent Attacks */}
                  {attackData?.ongoing && attackData.ongoing.map((attack: any, index: number) => (
                    <div key={`ongoing-${index}`} style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0.75rem',
                      marginBottom: '0.5rem',
                      background: 'rgba(220, 38, 38, 0.1)',
                      border: '1px solid rgba(220, 38, 38, 0.3)',
                      borderRadius: '4px'
                    }}>
                      <div style={{ marginRight: '1rem' }}>
                        <div style={{
                          width: '12px',
                          height: '12px',
                          background: '#dc2626',
                          borderRadius: '50%',
                          animation: 'pulse 2s infinite'
                        }}></div>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '0.875rem', fontWeight: 'bold', color: 'var(--color-critical)' }}>
                          {attack.startTime} - ONGOING
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
                          {attack.description} em {attack.ipAddress} • {attack.peakVolume}
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Completed Attacks */}
                  {attackData?.completed && attackData.completed.slice(0, 10).map((attack: any, index: number) => (
                    <div key={`completed-${index}`} style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0.75rem',
                      marginBottom: '0.5rem',
                      background: 'var(--color-bg-elevated)',
                      border: '1px solid var(--color-border)',
                      borderRadius: '4px'
                    }}>
                      <div style={{ marginRight: '1rem' }}>
                        <div style={{
                          width: '12px',
                          height: '12px',
                          background: '#16a34a',
                          borderRadius: '50%'
                        }}></div>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '0.875rem', fontWeight: 'bold', color: 'var(--color-success)' }}>
                          {attack.startTime} - PROTEGIDO
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
                          {attack.description} em {attack.ipAddress} • {attack.peakVolume} • {attack.duration}
                        </div>
                      </div>
                    </div>
                  ))}

                  {(!attackData?.ongoing || attackData.ongoing.length === 0) &&
                   (!attackData?.completed || attackData.completed.length === 0) && (
                    <div style={{
                      textAlign: 'center',
                      padding: '2rem',
                      color: 'var(--color-success)',
                      fontWeight: 'bold'
                    }}>
                      🟢 TODOS OS CLIENTES PROTEGIDOS - SISTEMA SEGURO
                    </div>
                  )}
                </div>
              </div>

              {/* Quick Response Panel (RESTORED) */}
              <div style={{
                background: 'var(--color-bg-card)',
                border: '1px solid var(--color-border)',
                borderRadius: '8px',
                padding: '1.5rem',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
              }}>
                <h4 style={{
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  color: 'var(--color-text-primary)',
                  marginBottom: '1rem',
                  borderBottom: '2px solid var(--color-border)',
                  paddingBottom: '0.5rem'
                }}>
                  ⚡ RESPOSTA RÁPIDA
                </h4>

                <div style={{ display: 'grid', gap: '0.75rem' }}>
                  <button
                    onClick={() => {
                      setActiveModule('protection');
                      setFlowspecTab('create');
                    }}
                    style={{
                      background: '#dc2626',
                      color: 'white',
                      border: 'none',
                      padding: '0.75rem',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      textAlign: 'left'
                    }}
                  >
                    🛡️ Criar Proteção
                  </button>

                  <button
                    onClick={() => {
                      setActiveModule('protection');
                      setFlowspecTab('manage');
                    }}
                    style={{
                      background: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      padding: '0.75rem',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      textAlign: 'left'
                    }}
                  >
                    📋 Gerenciar Regras
                  </button>

                  <button
                    onClick={() => setActiveModule('monitoring')}
                    style={{
                      background: '#8b5cf6',
                      color: 'white',
                      border: 'none',
                      padding: '0.75rem',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      textAlign: 'left'
                    }}
                  >
                    📊 Ver Monitoramento
                  </button>

                  <button
                    onClick={fetchMonitoringData}
                    style={{
                      background: '#10b981',
                      color: 'white',
                      border: 'none',
                      padding: '0.75rem',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      textAlign: 'left'
                    }}
                  >
                    🔄 Atualizar Dashboard
                  </button>

                  <div style={{
                    background: 'var(--color-bg-elevated)',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    padding: '0.75rem',
                    marginTop: '1rem'
                  }}>
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', marginBottom: '0.5rem' }}>
                      <strong>STATUS PLATAFORMA:</strong>
                    </div>
                    <div style={{ fontSize: '0.875rem' }}>
                      {attackData?.ongoing?.length > 0 ? (
                        <span style={{ color: 'var(--color-critical)', fontWeight: 'bold' }}>
                          🔴 {attackData.ongoing.length} CLIENTE{attackData.ongoing.length > 1 ? 'S' : ''} SOB ATAQUE
                        </span>
                      ) : (
                        <span style={{ color: 'var(--color-success)', fontWeight: 'bold' }}>
                          🟢 TODOS OS CLIENTES PROTEGIDOS
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* SOC Alert Banner (RESTORED) */}
            {attackData?.ongoing?.length > 0 && (
              <div style={{
                background: 'linear-gradient(90deg, #dc2626, #ef4444)',
                color: 'white',
                padding: '1rem',
                borderRadius: '8px',
                textAlign: 'center',
                marginBottom: '1rem',
                animation: 'pulse 3s infinite'
              }}>
                <div style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                  🚨 ALERTA CRÍTICO - CLIENTES SOFTDADOS SOB ATAQUE
                </div>
                <div style={{ fontSize: '0.875rem' }}>
                  {attackData.ongoing.length} ataque{attackData.ongoing.length > 1 ? 's' : ''} ativo{attackData.ongoing.length > 1 ? 's' : ''} detectado{attackData.ongoing.length > 1 ? 's' : ''} •
                  Proteção SOFTDADOS em ação • Equipe SOC monitorando
                </div>
              </div>
            )}
          </div>
        )}

        {/* ==================== HISTÓRICO DE ATAQUES ==================== */}
        {activeModule === 'historico' && (
          <div>
            {/* Attack History Panel - Redesigned with Dark Theme */}
            <div style={{
              background: 'var(--gradient-card)',
              border: '1px solid var(--color-border-light)',
              borderRadius: 'var(--radius-xl)',
              padding: 'var(--spacing-6)',
              marginBottom: 'var(--spacing-8)',
              boxShadow: 'var(--shadow-lg)',
              borderLeft: '4px solid var(--color-primary)'
            }}>
              {/* Panel Header with Controls */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 'var(--spacing-6)',
                paddingBottom: 'var(--spacing-4)',
                borderBottom: '1px solid var(--color-border)',
                flexWrap: 'wrap',
                gap: 'var(--spacing-4)'
              }}>
                <h3 style={{
                  fontSize: 'var(--text-xl)',
                  fontWeight: 'var(--font-bold)',
                  color: 'var(--color-text-primary)',
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-3)'
                }}>
                  📜 Histórico de Ataques
                </h3>
                <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center', flexWrap: 'wrap' }}>
                  <select
                    value={historyPeriod}
                    onChange={(e) => fetchAttackHistory(e.target.value, 0)}
                    className="btn btn-secondary btn-sm"
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-4)',
                      borderRadius: 'var(--radius-lg)',
                      fontSize: 'var(--text-sm)',
                      fontFamily: 'var(--font-primary)'
                    }}
                  >
                    <option value="24h">Últimas 24h</option>
                    <option value="7d">Últimos 7 dias</option>
                    <option value="30d">Últimos 30 dias</option>
                  </select>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => fetchAttackHistory(historyPeriod, 0)}
                    disabled={historyLoading}
                    style={{
                      opacity: historyLoading ? 0.5 : 1,
                      cursor: historyLoading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {historyLoading ? '⏳ Carregando...' : '🔄 Atualizar'}
                  </button>
                </div>
              </div>

              {/* Content Area */}
              {historyLoading ? (
                <div style={{
                  textAlign: 'center',
                  padding: 'var(--spacing-12)',
                  color: 'var(--color-text-tertiary)'
                }}>
                  <div className="animate-pulse" style={{ fontSize: 'var(--text-4xl)', marginBottom: 'var(--spacing-4)' }}>
                    ⏳
                  </div>
                  <div>Carregando histórico de ataques...</div>
                </div>
              ) : attackHistory.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: 'var(--spacing-12)',
                  color: 'var(--color-text-tertiary)'
                }}>
                  <div style={{ fontSize: 'var(--text-4xl)', marginBottom: 'var(--spacing-4)' }}>
                    📭
                  </div>
                  <div>Nenhum ataque encontrado no período selecionado</div>
                </div>
              ) : (
                <>
                  {/* Modern Table */}
                  <div style={{
                    background: 'var(--color-bg-elevated)',
                    borderRadius: 'var(--radius-lg)',
                    overflow: 'hidden',
                    border: '1px solid var(--color-border)'
                  }}>
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Status</th>
                          <th>IP Alvo</th>
                          <th>Descrição</th>
                          <th>Peak</th>
                          <th>Duração</th>
                          <th>Início</th>
                          <th>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {attackHistory.map((attack, index) => (
                          <tr key={attack.id || index}>
                            <td>
                              <span className={`badge ${attack.status === 'Ongoing' ? 'badge-critical' : 'badge-success'}`}>
                                {attack.status === 'Ongoing' ? '🔴 Ativo' : '✅ Completo'}
                              </span>
                            </td>
                            <td style={{
                              fontFamily: 'var(--font-mono)',
                              fontWeight: 'var(--font-bold)',
                              color: 'var(--color-primary-light)'
                            }}>
                              {attack.ipAddress}
                            </td>
                            <td style={{
                              fontSize: 'var(--text-sm)',
                              color: 'var(--color-text-primary)'
                            }}>
                              {attack.description}
                            </td>
                            <td style={{
                              fontSize: 'var(--text-sm)',
                              fontWeight: 'var(--font-bold)',
                              color: 'var(--color-critical-light)'
                            }}>
                              {attack.peakVolume}
                            </td>
                            <td style={{
                              fontSize: 'var(--text-sm)',
                              color: 'var(--color-text-secondary)'
                            }}>
                              {attack.duration}
                            </td>
                            <td style={{
                              fontSize: 'var(--text-xs)',
                              color: 'var(--color-text-tertiary)'
                            }}>
                              {attack.startTime}
                            </td>
                            <td>
                              <button
                                className="btn btn-primary btn-sm"
                                onClick={() => fetchAIConfigs(attack)}
                                disabled={loading}
                                style={{
                                  background: loading ? 'var(--color-bg-tertiary)' : 'var(--gradient-hero)',
                                  opacity: loading ? 0.5 : 1,
                                  cursor: loading ? 'not-allowed' : 'pointer',
                                  border: 'none'
                                }}
                              >
                                🤖 Configs
                              </button>
                              <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => queryKentikData(attack)}
                                style={{
                                  marginLeft: '8px',
                                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                  border: 'none',
                                  color: 'white'
                                }}
                                title="Abrir análise no Kentik Data Explorer"
                              >
                                📊 View in Kentik
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {historyPagination.hasMore && (
                    <div style={{ textAlign: 'center', marginTop: 'var(--spacing-4)' }}>
                      <button
                        className="btn btn-secondary"
                        onClick={() => fetchAttackHistory(historyPeriod, historyPagination.offset + historyPagination.limit)}
                        disabled={historyLoading}
                        style={{
                          opacity: historyLoading ? 0.5 : 1,
                          cursor: historyLoading ? 'not-allowed' : 'pointer'
                        }}
                      >
                        📄 Carregar Mais ({historyPagination.total - historyPagination.offset - historyPagination.limit} restantes)
                      </button>
                    </div>
                  )}

                  {/* Stats */}
                  <div style={{
                    fontSize: 'var(--text-xs)',
                    color: 'var(--color-text-muted)',
                    marginTop: 'var(--spacing-3)',
                    textAlign: 'center',
                    padding: 'var(--spacing-2)',
                    background: 'var(--color-bg-elevated)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--color-border)'
                  }}>
                    Mostrando <strong style={{ color: 'var(--color-primary-light)' }}>{attackHistory.length}</strong> de <strong style={{ color: 'var(--color-primary-light)' }}>{historyPagination.total}</strong> ataques
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* ==================== PROTECTION MANAGER (RESTORED ENTERPRISE) ==================== */}
        {activeModule === 'protection' && (
          <div>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: 'bold',
              marginBottom: '1rem',
              color: 'var(--color-text-primary)',
              borderLeft: '4px solid #10b981',
              paddingLeft: '1rem'
            }}>
              🛡️ Protection Manager - Proteção DDoS Inteligente
            </h2>
            <p style={{
              color: 'var(--color-text-secondary)',
              marginBottom: '1rem',
              fontSize: '0.875rem',
              fontStyle: 'italic'
            }}>
              Direcionamento inteligente para Scrubbing Centers • Preserva tráfego legítimo • Foca em anomalias
            </p>

            {/* FlowSpec Sub-tabs (RESTORED) */}
            <div style={{
              display: 'flex',
              borderBottom: '1px solid #e5e7eb',
              marginBottom: '2rem'
            }}>
              <button
                onClick={() => setFlowspecTab('create')}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  background: flowspecTab === 'create' ? '#10b981' : 'transparent',
                  color: flowspecTab === 'create' ? 'white' : '#6b7280',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  borderRadius: '6px 6px 0 0'
                }}
              >
                ➕ Criar Regras
              </button>
              <button
                onClick={() => setFlowspecTab('manage')}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  background: flowspecTab === 'manage' ? '#10b981' : 'transparent',
                  color: flowspecTab === 'manage' ? 'white' : '#6b7280',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  borderRadius: '6px 6px 0 0'
                }}
              >
                📋 Gerenciar Regras
              </button>
            </div>

            {/* Create Tab (RESTORED) */}
            {flowspecTab === 'create' && (
              <>
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'block',
                    fontWeight: 'bold',
                    marginBottom: '0.5rem',
                    color: 'var(--color-text-secondary)'
                  }}>
                    Destino(s) para Proteção SOFTDADOS:
                  </label>
                  <textarea
                    value={destination}
                    onChange={(e) => setDestination(e.target.value)}
                    placeholder="ex: 187.87.33.0/24, 177.8.131.0/24, 10.0.0.0/16
Use IPs da SOC Dashboard para ataques ativos
Múltiplas subredes: vírgula ou quebra de linha"
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '1rem',
                      fontFamily: 'monospace',
                      resize: 'vertical'
                    }}
                  />
                  <small style={{ color: 'var(--color-text-secondary)', marginTop: '0.25rem', display: 'block' }}>
                    <strong>📝 Múltiplas subredes:</strong> Separe por vírgula ou quebra de linha<br/>
                    <strong>📦 Divisão automática:</strong> Subredes maiores que /24 são divididas automaticamente<br/>
                    <strong>🔍 Verificação:</strong> Regras duplicadas são automaticamente puladas
                  </small>
                </div>

                {/* AI Analysis Section (RESTORED) */}
                {destination.trim() && (
                  <div style={{
                    background: 'rgba(59, 130, 246, 0.1)',
                    border: '1px solid #0ea5e9',
                    borderRadius: '8px',
                    padding: '1rem',
                    marginBottom: '1rem'
                  }}>
                    <h4 style={{ fontSize: '1rem', fontWeight: 'bold', color: 'var(--color-primary)', marginBottom: '0.5rem' }}>
                      🧠 Análise Inteligente SOFTDADOS
                    </h4>
                    <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginBottom: '1rem' }}>
                      Nossa IA analisará ataques ativos contra clientes e recomendará proteção específica.
                    </p>
                    <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                      <button
                        onClick={async () => {
                          const recommendations = await getAIRecommendations(destination.trim());
                          if (recommendations) {
                            if (recommendations.analysis.activeAttacksFound > 0) {
                              alert(`🔴 ATAQUE ATIVO DETECTADO!\n\n${recommendations.analysis.activeAttacksFound} ataque(s) ativo(s) contra cliente SOFTDADOS.\n\nConfiança IA: ${recommendations.analysis.confidence}%\nRecomendação: ${recommendations.recommendations.critical.length} templates críticos.\n\nClique "Preview Regras" para revisar antes de implementar.`);
                            } else {
                              alert(`🟡 ANÁLISE PREVENTIVA\n\nNenhum ataque ativo detectado para este destino.\n\nConfiança: ${recommendations.analysis.confidence}%\nRecomendação: ${recommendations.recommendations.critical.length} templates básicos + ${recommendations.recommendations.preventive.length} opcionais.\n\nClique "Preview Regras" para revisar antes de implementar.`);
                            }
                          }
                        }}
                        style={{
                          background: '#dc2626',
                          color: 'white',
                          padding: '0.5rem 1rem',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '0.875rem',
                          fontWeight: 'bold',
                          cursor: 'pointer'
                        }}
                      >
                        🧠 Análise IA + Recomendações
                      </button>
                      <button
                        onClick={generatePreview}
                        disabled={!destination.trim()}
                        style={{
                          background: !destination.trim() ? '#9ca3af' : '#f59e0b',
                          color: 'white',
                          padding: '0.5rem 1rem',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '0.875rem',
                          fontWeight: 'bold',
                          cursor: !destination.trim() ? 'not-allowed' : 'pointer'
                        }}
                      >
                        👁️ Preview Regras
                      </button>
                      <button
                        onClick={createRules}
                        disabled={loading}
                        style={{
                          background: loading ? '#9ca3af' : '#10b981',
                          color: 'white',
                          padding: '0.5rem 1rem',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '0.875rem',
                          fontWeight: 'bold',
                          cursor: loading ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {loading ? 'Implementando...' : '🚀 Criar Direto (Legacy)'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Result Display (RESTORED) */}
                {result && (
                  <div style={{
                    background: 'rgba(16, 185, 129, 0.1)',
                    border: '1px solid #bbf7d0',
                    color: 'var(--color-success-dark)',
                    padding: '1rem',
                    borderRadius: '6px',
                    marginBottom: '1rem'
                  }}>
                    <h3 style={{ marginBottom: '0.5rem', fontWeight: 'bold' }}>
                      ✅ Proteção SOFTDADOS Implementada!
                    </h3>
                    <p>{result.message}</p>
                    <div style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                      <p><strong>Destinos processados:</strong> {result.destinationsProcessed}</p>
                      <p><strong>Regras criadas:</strong> {result.rulesCreated}</p>
                      {result.rulesSkipped > 0 && (
                        <p><strong>Regras puladas (já existiam):</strong> {result.rulesSkipped}</p>
                      )}
                      <p><strong>Total analisado:</strong> {result.totalRules}</p>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Manage Tab (RESTORED WITH BATCH OPERATIONS) */}
            {flowspecTab === 'manage' && (
              <div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '1.5rem'
                }}>
                  <div>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--color-text-primary)', margin: 0 }}>
                      Regras de Proteção SOFTDADOS
                    </h3>
                    <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', margin: '0.25rem 0 0 0' }}>
                      {rules.length} regras • {selectedRules.size} selecionadas
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    {selectedRules.size > 0 && (
                      <button
                        onClick={removeBatchRules}
                        disabled={batchLoading}
                        style={{
                          background: batchLoading ? '#9ca3af' : '#dc2626',
                          color: 'white',
                          padding: '0.5rem 1rem',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '0.875rem',
                          cursor: batchLoading ? 'not-allowed' : 'pointer',
                          fontWeight: 'bold'
                        }}
                      >
                        {batchLoading ? '🗑️ Removendo...' : `🗑️ Remover ${selectedRules.size}`}
                      </button>
                    )}
                    <button
                      onClick={fetchRules}
                      disabled={rulesLoading}
                      style={{
                        background: '#10b981',
                        color: 'white',
                        padding: '0.5rem 1rem',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '0.875rem',
                        cursor: rulesLoading ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {rulesLoading ? '🔄 Carregando...' : '🔄 Atualizar'}
                    </button>
                  </div>
                </div>

                {/* Search and Batch Controls (RESTORED) */}
                {rules.length > 0 && (
                  <div style={{
                    background: 'var(--color-bg-elevated)',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    padding: '1rem',
                    marginBottom: '1rem'
                  }}>
                    <div style={{ marginBottom: '1rem' }}>
                      <input
                        type="text"
                        placeholder="🔍 Filtrar regras (por nome, destino, tipo...)"
                        value={rulesFilter}
                        onChange={(e) => setRulesFilter(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          fontSize: '0.875rem'
                        }}
                      />
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                      <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={selectedRules.size === filteredRules.length && filteredRules.length > 0}
                          onChange={toggleSelectAll}
                          style={{ marginRight: '0.5rem' }}
                        />
                        <span style={{ fontWeight: 'bold', color: 'var(--color-text-secondary)' }}>
                          {selectedRules.size === filteredRules.length && filteredRules.length > 0 ? 'Desselecionar Todas' : 'Selecionar Todas'}
                        </span>
                      </label>

                      <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                        {selectedRules.size} selecionadas de {filteredRules.length} filtradas ({rules.length} total)
                      </div>

                      {selectedRules.size > 0 && (
                        <button
                          onClick={() => setSelectedRules(new Set())}
                          style={{
                            background: 'transparent',
                            color: 'var(--color-text-secondary)',
                            border: '1px solid #d1d5db',
                            padding: '0.25rem 0.75rem',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            cursor: 'pointer'
                          }}
                        >
                          Limpar Seleção
                        </button>
                      )}

                      {/* Quick Selection Buttons (RESTORED) */}
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => {
                            const inactiveRules = filteredRules.filter(r => r.state !== 'active').map(r => r.name);
                            setSelectedRules(new Set(inactiveRules));
                          }}
                          style={{
                            background: '#f59e0b',
                            color: 'white',
                            border: 'none',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            cursor: 'pointer'
                          }}
                        >
                          Selecionar Inativas
                        </button>
                        <button
                          onClick={() => {
                            const oldRules = filteredRules.filter(r => {
                              if (!r.firstAddedTime) return false;
                              const created = new Date(r.firstAddedTime);
                              const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
                              return created < oneWeekAgo;
                            }).map(r => r.name);
                            setSelectedRules(new Set(oldRules));
                          }}
                          style={{
                            background: '#8b5cf6',
                            color: 'white',
                            border: 'none',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            cursor: 'pointer'
                          }}
                        >
                          Selecionar Antigas (7+ dias)
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Rules List (RESTORED WITH MULTI-SELECT) */}
                {rulesLoading ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-secondary)' }}>
                    Carregando regras de proteção...
                  </div>
                ) : (
                  <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                    {filteredRules.length === 0 ? (
                      <div style={{
                        textAlign: 'center',
                        padding: '2rem',
                        color: 'var(--color-text-secondary)',
                        background: 'var(--color-bg-elevated)',
                        borderRadius: '6px'
                      }}>
                        {rules.length === 0 ? 'Nenhuma regra encontrada' : 'Nenhuma regra corresponde ao filtro'}
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gap: '0.75rem' }}>
                        {filteredRules.map((rule, index) => (
                          <div key={rule.name || index} style={{
                            background: selectedRules.has(rule.name) ? '#f0f9ff' : '#f9fafb',
                            border: selectedRules.has(rule.name) ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                            borderRadius: '6px',
                            padding: '1rem',
                            transition: 'all 0.2s ease'
                          }}>
                            <div style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: '1rem',
                              marginBottom: '0.5rem'
                            }}>
                              {/* Selection Checkbox (RESTORED) */}
                              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginTop: '0.25rem' }}>
                                <input
                                  type="checkbox"
                                  checked={selectedRules.has(rule.name)}
                                  onChange={() => toggleRuleSelection(rule.name)}
                                  style={{
                                    marginRight: '0.5rem',
                                    transform: 'scale(1.2)'
                                  }}
                                />
                              </label>

                              {/* Rule Info */}
                              <div style={{ flex: 1 }}>
                                <div style={{
                                  fontWeight: 'bold',
                                  fontSize: '0.875rem',
                                  color: 'var(--color-text-primary)',
                                  marginBottom: '0.25rem'
                                }}>
                                  {rule.name}
                                </div>
                                <div style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '4px',
                                  fontSize: '0.75rem',
                                  fontWeight: 'bold',
                                  background: rule.state === 'active' ? '#dcfce7' : '#fef3c7',
                                  color: rule.state === 'active' ? '#166534' : '#92400e'
                                }}>
                                  {rule.state === 'active' ? '✅ Protegendo' : '⚠️ Inativo'}
                                </div>
                              </div>

                              {/* Individual Remove Button */}
                              <button
                                onClick={() => {
                                  if (window.confirm(`Remover regra: ${rule.name}?`)) {
                                    // Individual remove logic here
                                    alert('Funcionalidade de remoção individual mantida');
                                  }
                                }}
                                style={{
                                  background: '#dc2626',
                                  color: 'white',
                                  border: 'none',
                                  padding: '0.5rem 0.75rem',
                                  borderRadius: '4px',
                                  fontSize: '0.75rem',
                                  cursor: 'pointer',
                                  fontWeight: 'bold'
                                }}
                              >
                                🗑️ Remover
                              </button>
                            </div>

                            <div style={{
                              fontSize: '0.75rem',
                              color: 'var(--color-text-secondary)',
                              fontFamily: 'monospace',
                              background: 'white',
                              padding: '0.5rem',
                              borderRadius: '4px',
                              marginBottom: '0.5rem',
                              wordBreak: 'break-all'
                            }}>
                              {rule.match}
                            </div>

                            {rule.description && (
                              <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
                                {rule.description}
                              </div>
                            )}

                            {rule.firstAddedTime && (
                              <div style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', marginTop: '0.25rem' }}>
                                Criado em: {new Date(rule.firstAddedTime).toLocaleString()}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ==================== MONITORING MODULE (RESTORED) ==================== */}
        {activeModule === 'monitoring' && (
          <div>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: 'bold',
              marginBottom: '1rem',
              color: 'var(--color-text-primary)',
              borderLeft: '4px solid #8b5cf6',
              paddingLeft: '1rem'
            }}>
              📊 Monitoring - Dashboard em Tempo Real
            </h2>

            {/* Refresh Controls (RESTORED) */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '2rem',
              padding: '1rem',
              background: 'var(--color-bg-elevated)',
              borderRadius: '6px',
              border: '1px solid #e2e8f0'
            }}>
              <div>
                <h3 style={{ fontSize: '1rem', fontWeight: 'bold', color: 'var(--color-text-primary)', margin: 0 }}>
                  Status da Plataforma SOFTDADOS
                </h3>
                <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', margin: '0.25rem 0 0 0' }}>
                  {lastUpdate ? `Última atualização: ${lastUpdate}` : 'Aguardando dados...'}
                </p>
              </div>
              <button
                onClick={fetchMonitoringData}
                disabled={monitoringLoading}
                style={{
                  background: monitoringLoading ? '#9ca3af' : '#8b5cf6',
                  color: 'white',
                  padding: '0.5rem 1rem',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  cursor: monitoringLoading ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold'
                }}
              >
                {monitoringLoading ? '🔄 Atualizando...' : '🔄 Atualizar Agora'}
              </button>
            </div>

            {/* Monitoring Widgets (RESTORED) */}
            {monitoringData?.systemHealth && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '1rem',
                marginBottom: '2rem'
              }}>
                {/* System Health Widget */}
                <div style={{
                  background: 'white',
                  padding: '1.5rem',
                  borderRadius: '8px',
                  border: '1px solid var(--color-border)',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                }}>
                  <h4 style={{
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    color: 'var(--color-text-primary)',
                    marginBottom: '1rem',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    🏥 System Health
                    <span style={{
                      marginLeft: 'auto',
                      fontSize: '0.75rem',
                      color: monitoringData?.systemHealth ? '#10b981' : '#ef4444',
                      fontWeight: 'normal'
                    }}>
                      {monitoringData?.systemHealth ? '● Online' : '● Offline'}
                    </span>
                  </h4>

                  <div style={{ fontSize: '0.875rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <span>Sistema:</span>
                      <span style={{ fontFamily: 'monospace' }}>{monitoringData.systemHealth.version}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <span>Carga:</span>
                      <span style={{ fontFamily: 'monospace', color: monitoringData.systemHealth.systemLoad > 1 ? '#ef4444' : '#10b981' }}>
                        {monitoringData.systemHealth.systemLoad.toFixed(2)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <span>Memória:</span>
                      <span style={{ fontFamily: 'monospace', color: monitoringData.systemHealth.memoryUsage > 80 ? '#ef4444' : '#10b981' }}>
                        {monitoringData.systemHealth.memoryUsage.toFixed(1)}%
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Usuários:</span>
                      <span style={{ fontFamily: 'monospace' }}>
                        {monitoringData.systemHealth.loggedInUsers}/{monitoringData.systemHealth.totalUsers}
                      </span>
                    </div>
                  </div>
                </div>

                {/* FlowSpec Stats Widget (RESTORED) */}
                <div style={{
                  background: 'white',
                  padding: '1.5rem',
                  borderRadius: '8px',
                  border: '1px solid var(--color-border)',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                }}>
                  <h4 style={{
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    color: 'var(--color-text-primary)',
                    marginBottom: '1rem',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    🛡️ Protection Rules
                    <span style={{
                      marginLeft: 'auto',
                      fontSize: '0.75rem',
                      color: monitoringData?.flowspecStats ? '#10b981' : '#ef4444',
                      fontWeight: 'normal'
                    }}>
                      {monitoringData?.flowspecStats ? '● Active' : '● Error'}
                    </span>
                  </h4>

                  {monitoringData?.flowspecStats ? (
                    <div style={{ fontSize: '0.875rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                        <span>Total de Regras:</span>
                        <span style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                          {monitoringData.flowspecStats.totalRules}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                        <span>Ativas:</span>
                        <span style={{ fontFamily: 'monospace', color: 'var(--color-success)' }}>
                          {monitoringData.flowspecStats.activeRules}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                        <span>Inativas:</span>
                        <span style={{ fontFamily: 'monospace', color: 'var(--color-warning)' }}>
                          {monitoringData.flowspecStats.inactiveRules}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Criadas (24h):</span>
                        <span style={{ fontFamily: 'monospace', color: 'var(--color-info)' }}>
                          {monitoringData.flowspecStats.recentRulesCount}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div style={{ color: 'var(--color-text-secondary)', textAlign: 'center', padding: '1rem' }}>
                      Dados indisponíveis
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ==================== REPORTS MODULE (PROFESSIONAL) ==================== */}
        {activeModule === 'reports' && (
          <ProfessionalReportsModule
            activeReport={activeReport}
            setActiveReport={setActiveReport}
          />
        )}

        {/* ==================== MODALS (RESTORED) ==================== */}

        {/* Intelligence Report Modal (RESTORED) */}
        {showingReport && intelligenceReport && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '2rem',
              maxWidth: '90vw',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '2rem',
                borderBottom: '2px solid var(--color-border)',
                paddingBottom: '1rem'
              }}>
                <h2 style={{
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  color: 'var(--color-text-primary)',
                  margin: 0
                }}>
                  🧠 RELATÓRIO DE INTELIGÊNCIA - 24 HORAS
                </h2>
                <button
                  onClick={() => setShowingReport(false)}
                  style={{
                    background: '#dc2626',
                    color: 'white',
                    border: 'none',
                    padding: '0.5rem 1rem',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                >
                  ✕ Fechar
                </button>
              </div>

              {/* AI Badge */}
              {intelligenceReport.intelligence?.aiGenerated && (
                <div style={{
                  background: 'linear-gradient(90deg, #8b5cf6, #3b82f6)',
                  color: 'white',
                  padding: '0.75rem 1rem',
                  borderRadius: '6px',
                  textAlign: 'center',
                  fontWeight: 'bold',
                  marginBottom: '1.5rem'
                }}>
                  🤖 Análise gerada por Inteligência Artificial
                </div>
              )}

              {/* Summary Stats */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1rem',
                marginBottom: '2rem'
              }}>
                <div style={{ background: 'rgba(220, 38, 38, 0.1)', padding: '1rem', borderRadius: '6px', textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--color-critical)' }}>
                    {intelligenceReport.attacksAnalyzed || intelligenceReport.intelligence?.attacksAnalyzed || 0}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>Ataques Analisados</div>
                </div>
                <div style={{ background: 'rgba(16, 185, 129, 0.1)', padding: '1rem', borderRadius: '6px', textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--color-success)' }}>
                    {intelligenceReport.intelligence?.threatLevel?.label || 'N/A'}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>Nível de Ameaça</div>
                </div>
                <div style={{ background: 'rgba(59, 130, 246, 0.1)', padding: '1rem', borderRadius: '6px', textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--color-primary)' }}>
                    {intelligenceReport.intelligence?.threatLevel?.score || 0}/10
                  </div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>Score de Ameaça</div>
                </div>
                <div style={{ background: 'rgba(245, 158, 11, 0.1)', padding: '1rem', borderRadius: '6px', textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--color-warning)' }}>
                    {intelligenceReport.intelligence?.threatLevel?.trend || 'N/A'}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>Tendência</div>
                </div>
              </div>

              {/* Threat Justification */}
              {intelligenceReport.intelligence?.threatLevel?.justification && (
                <div style={{
                  background: 'rgba(220, 38, 38, 0.1)',
                  border: '1px solid #dc2626',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '1.5rem'
                }}>
                  <h3 style={{ margin: '0 0 0.5rem 0', color: 'var(--color-critical)', fontSize: '1rem' }}>
                    📋 Justificativa do Nível de Ameaça
                  </h3>
                  <p style={{ margin: 0, color: 'var(--color-text-secondary)', lineHeight: '1.6' }}>
                    {intelligenceReport.intelligence.threatLevel.justification}
                  </p>
                </div>
              )}

              {/* Patterns Identified */}
              {intelligenceReport.intelligence?.patterns && intelligenceReport.intelligence.patterns.length > 0 && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <h3 style={{ margin: '0 0 1rem 0', color: 'var(--color-text-primary)', fontSize: '1.125rem', borderBottom: '2px solid var(--color-border)', paddingBottom: '0.5rem' }}>
                    🔍 Padrões Identificados ({intelligenceReport.intelligence.patterns.length})
                  </h3>
                  {intelligenceReport.intelligence.patterns.map((pattern: any, index: number) => (
                    <div key={index} style={{
                      background: 'var(--color-bg-elevated)',
                      border: '1px solid var(--color-border)',
                      borderRadius: '6px',
                      padding: '1rem',
                      marginBottom: '1rem'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <strong style={{ color: 'var(--color-text-primary)' }}>{pattern.name}</strong>
                        <span style={{
                          background: pattern.confidence >= 80 ? '#10b981' : pattern.confidence >= 60 ? '#f59e0b' : '#6b7280',
                          color: 'white',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: 'bold'
                        }}>
                          {pattern.confidence}% confiança
                        </span>
                      </div>
                      <p style={{ margin: '0 0 0.5rem 0', color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
                        {pattern.description}
                      </p>
                      {pattern.indicators && pattern.indicators.length > 0 && (
                        <div style={{ marginTop: '0.5rem' }}>
                          <strong style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>Indicadores:</strong>
                          <ul style={{ margin: '0.25rem 0 0 0', paddingLeft: '1.5rem', fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
                            {pattern.indicators.map((ind: string, i: number) => (
                              <li key={i}>{ind}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Predictions */}
              {intelligenceReport.intelligence?.predictions && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <h3 style={{ margin: '0 0 1rem 0', color: 'var(--color-text-primary)', fontSize: '1.125rem', borderBottom: '2px solid var(--color-border)', paddingBottom: '0.5rem' }}>
                    🔮 Predições e Análise Preditiva
                  </h3>
                  <div style={{ background: 'rgba(59, 130, 246, 0.1)', border: '1px solid var(--color-primary)', borderRadius: '6px', padding: '1rem' }}>
                    {intelligenceReport.intelligence.predictions.likelyVectors && (
                      <div style={{ marginBottom: '1rem' }}>
                        <strong style={{ color: 'var(--color-text-primary)', display: 'block', marginBottom: '0.5rem' }}>
                          Vetores Prováveis:
                        </strong>
                        <ul style={{ margin: 0, paddingLeft: '1.5rem', color: 'var(--color-text-secondary)' }}>
                          {intelligenceReport.intelligence.predictions.likelyVectors.map((v: string, i: number) => (
                            <li key={i}>{v}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {intelligenceReport.intelligence.predictions.targetsAtRisk && (
                      <div style={{ marginBottom: '1rem' }}>
                        <strong style={{ color: 'var(--color-text-primary)', display: 'block', marginBottom: '0.5rem' }}>
                          Alvos em Risco:
                        </strong>
                        <ul style={{ margin: 0, paddingLeft: '1.5rem', color: 'var(--color-text-secondary)' }}>
                          {intelligenceReport.intelligence.predictions.targetsAtRisk.map((t: string, i: number) => (
                            <li key={i}>{t}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {intelligenceReport.intelligence.predictions.preventiveActions && (
                      <div>
                        <strong style={{ color: 'var(--color-text-primary)', display: 'block', marginBottom: '0.5rem' }}>
                          Ações Preventivas Recomendadas:
                        </strong>
                        <ul style={{ margin: 0, paddingLeft: '1.5rem', color: 'var(--color-text-secondary)' }}>
                          {intelligenceReport.intelligence.predictions.preventiveActions.map((a: string, i: number) => (
                            <li key={i}>{a}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Defense Priorities */}
              {intelligenceReport.intelligence?.defensePriorities && intelligenceReport.intelligence.defensePriorities.length > 0 && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <h3 style={{ margin: '0 0 1rem 0', color: 'var(--color-text-primary)', fontSize: '1.125rem', borderBottom: '2px solid var(--color-border)', paddingBottom: '0.5rem' }}>
                    🛡️ Prioridades de Defesa
                  </h3>
                  {intelligenceReport.intelligence.defensePriorities.map((priority: any, index: number) => (
                    <div key={index} style={{
                      background: priority.urgency === 'alta' ? '#fef2f2' : priority.urgency === 'média' ? '#fef9f0' : '#f0fdf4',
                      border: `1px solid ${priority.urgency === 'alta' ? '#dc2626' : priority.urgency === 'média' ? '#f59e0b' : '#10b981'}`,
                      borderRadius: '6px',
                      padding: '1rem',
                      marginBottom: '0.75rem'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <strong style={{ color: 'var(--color-text-primary)' }}>{priority.action}</strong>
                        <span style={{
                          background: priority.urgency === 'alta' ? '#dc2626' : priority.urgency === 'média' ? '#f59e0b' : '#10b981',
                          color: 'white',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          textTransform: 'uppercase'
                        }}>
                          {priority.urgency}
                        </span>
                      </div>
                      <p style={{ margin: 0, color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
                        {priority.justification}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              <p style={{ color: 'var(--color-success-dark)', textAlign: 'center', fontWeight: 'bold', marginTop: '1rem' }}>
                Análise completa de {intelligenceReport.attacksAnalyzed || intelligenceReport.intelligence?.attacksAnalyzed || 0} ataques processados com IA pela plataforma SOFTDADOS
              </p>
            </div>
          </div>
        )}

        {/* Kentik Traffic Analysis Modal */}
        {showingKentikModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '2rem',
              maxWidth: '95vw',
              width: '1200px',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}>
              {/* Header */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '2rem',
                borderBottom: '3px solid',
                borderImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%) 1',
                paddingBottom: '1rem'
              }}>
                <h2 style={{
                  fontSize: '1.75rem',
                  fontWeight: 'bold',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  margin: 0
                }}>
                  📊 Kentik Traffic Analysis
                </h2>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={analyzeWithAI}
                    disabled={kentikLoading || kentikAILoading || !kentikAnalysis}
                    style={{
                      background: kentikAILoading ? '#94a3b8' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      color: 'white',
                      border: 'none',
                      padding: '0.5rem 1rem',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      fontWeight: 'bold',
                      cursor: (kentikLoading || kentikAILoading || !kentikAnalysis) ? 'not-allowed' : 'pointer',
                      opacity: (kentikLoading || !kentikAnalysis) ? 0.5 : 1
                    }}
                    title="Analisar dados com Gemini AI"
                  >
                    {kentikAILoading ? '🔄 Analisando...' : '🤖 Analisar com IA'}
                  </button>
                  <button
                    onClick={() => setShowingKentikModal(false)}
                    style={{
                      background: '#dc2626',
                      color: 'white',
                      border: 'none',
                      padding: '0.5rem 1rem',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    ✕ Fechar
                  </button>
                </div>
              </div>

              {/* Attack Info Summary */}
              {kentikAttackInfo && (
                <div style={{
                  background: 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)',
                  border: '1px solid #667eea30',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '1.5rem',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '1rem'
                }}>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>Target IP</div>
                    <div style={{ fontSize: '1rem', fontWeight: 'bold', color: '#1e293b' }}>{kentikAttackInfo.targetIP}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>Duration</div>
                    <div style={{ fontSize: '1rem', fontWeight: 'bold', color: '#1e293b' }}>{kentikAttackInfo.duration}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>Peak Volume</div>
                    <div style={{ fontSize: '1rem', fontWeight: 'bold', color: '#dc2626' }}>{kentikAttackInfo.peakVolume}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>Protocol</div>
                    <div style={{ fontSize: '1rem', fontWeight: 'bold', color: '#1e293b' }}>{kentikAttackInfo.protocol || 'N/A'}</div>
                  </div>
                </div>
              )}

              {/* Loading State */}
              {kentikLoading && (
                <div style={{
                  textAlign: 'center',
                  padding: '3rem',
                  color: '#64748b'
                }}>
                  <div style={{
                    fontSize: '3rem',
                    marginBottom: '1rem',
                    animation: 'spin 2s linear infinite'
                  }}>🔄</div>
                  <div style={{ fontSize: '1.125rem' }}>Consultando Kentik API...</div>
                  <div style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>Analisando 4 dimensões de tráfego</div>
                </div>
              )}

              {/* Results */}
              {!kentikLoading && kentikAnalysis && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem' }}>

                  {/* Section 1: Top Source IPs */}
                  <div style={{
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '1.5rem'
                  }}>
                    <h3 style={{
                      margin: '0 0 1rem 0',
                      fontSize: '1.125rem',
                      fontWeight: 'bold',
                      color: '#1e293b',
                      borderBottom: '2px solid #667eea',
                      paddingBottom: '0.5rem'
                    }}>
                      🎯 Top 10 Source IPs (Atacantes)
                    </h3>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', fontSize: '0.875rem' }}>
                        <thead>
                          <tr style={{ background: '#f1f5f9', borderBottom: '2px solid #cbd5e1' }}>
                            <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Source IP</th>
                            <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600' }}>Peak</th>
                            <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600' }}>Packets</th>
                          </tr>
                        </thead>
                        <tbody>
                          {kentikAnalysis.sourceIPs?.results?.[0]?.data?.slice(0, 10).map((ip: any, index: number) => (
                            <tr key={index} style={{
                              borderBottom: '1px solid #e2e8f0',
                              background: index % 2 === 0 ? 'white' : '#f8fafc'
                            }}>
                              <td style={{ padding: '0.75rem', fontFamily: 'monospace', color: '#0f172a' }}>
                                {ip.IP_src || ip.key || 'N/A'}
                              </td>
                              <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold', color: '#dc2626' }}>
                                {formatGbps(ip.max_bits_per_sec || 0)}
                              </td>
                              <td style={{ padding: '0.75rem', textAlign: 'right', color: '#64748b' }}>
                                {formatNumber(ip.sum_packets || 0)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {(!kentikAnalysis.sourceIPs?.results?.[0]?.data || kentikAnalysis.sourceIPs.results[0].data.length === 0) && (
                        <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>
                          ⚠️ Nenhum dado encontrado
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Section 2: Top Source Ports */}
                  <div style={{
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '1.5rem'
                  }}>
                    <h3 style={{
                      margin: '0 0 1rem 0',
                      fontSize: '1.125rem',
                      fontWeight: 'bold',
                      color: '#1e293b',
                      borderBottom: '2px solid #764ba2',
                      paddingBottom: '0.5rem'
                    }}>
                      🔌 Top 10 Source Ports
                    </h3>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', fontSize: '0.875rem' }}>
                        <thead>
                          <tr style={{ background: '#f1f5f9', borderBottom: '2px solid #cbd5e1' }}>
                            <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Port</th>
                            <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600' }}>Peak</th>
                            <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600' }}>Packets</th>
                          </tr>
                        </thead>
                        <tbody>
                          {kentikAnalysis.sourcePorts?.results?.[0]?.data?.slice(0, 10).map((port: any, index: number) => (
                            <tr key={index} style={{
                              borderBottom: '1px solid #e2e8f0',
                              background: index % 2 === 0 ? 'white' : '#f8fafc'
                            }}>
                              <td style={{ padding: '0.75rem', fontFamily: 'monospace', color: '#0f172a', fontWeight: 'bold' }}>
                                {port.Port_src || port.key || 'N/A'}
                              </td>
                              <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold', color: '#dc2626' }}>
                                {formatGbps(port.max_bits_per_sec || 0)}
                              </td>
                              <td style={{ padding: '0.75rem', textAlign: 'right', color: '#64748b' }}>
                                {formatNumber(port.sum_packets || 0)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {(!kentikAnalysis.sourcePorts?.results?.[0]?.data || kentikAnalysis.sourcePorts.results[0].data.length === 0) && (
                        <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>
                          ⚠️ Nenhum dado encontrado
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Section 3: Top Destination Ports */}
                  <div style={{
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '1.5rem'
                  }}>
                    <h3 style={{
                      margin: '0 0 1rem 0',
                      fontSize: '1.125rem',
                      fontWeight: 'bold',
                      color: '#1e293b',
                      borderBottom: '2px solid #10b981',
                      paddingBottom: '0.5rem'
                    }}>
                      🎯 Top Destination Ports (Alvos)
                    </h3>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', fontSize: '0.875rem' }}>
                        <thead>
                          <tr style={{ background: '#f1f5f9', borderBottom: '2px solid #cbd5e1' }}>
                            <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Port</th>
                            <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600' }}>Peak</th>
                            <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600' }}>Packets</th>
                          </tr>
                        </thead>
                        <tbody>
                          {kentikAnalysis.destPorts?.results?.[0]?.data?.slice(0, 10).map((port: any, index: number) => (
                            <tr key={index} style={{
                              borderBottom: '1px solid #e2e8f0',
                              background: index % 2 === 0 ? 'white' : '#f8fafc'
                            }}>
                              <td style={{ padding: '0.75rem', fontFamily: 'monospace', color: '#0f172a', fontWeight: 'bold' }}>
                                {port.Port_dst || port.key || 'N/A'}
                              </td>
                              <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold', color: '#dc2626' }}>
                                {formatGbps(port.max_bits_per_sec || 0)}
                              </td>
                              <td style={{ padding: '0.75rem', textAlign: 'right', color: '#64748b' }}>
                                {formatNumber(port.sum_packets || 0)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {(!kentikAnalysis.destPorts?.results?.[0]?.data || kentikAnalysis.destPorts.results[0].data.length === 0) && (
                        <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>
                          ⚠️ Nenhum dado encontrado
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Section 4: Protocol Breakdown */}
                  <div style={{
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '1.5rem'
                  }}>
                    <h3 style={{
                      margin: '0 0 1rem 0',
                      fontSize: '1.125rem',
                      fontWeight: 'bold',
                      color: '#1e293b',
                      borderBottom: '2px solid #f59e0b',
                      paddingBottom: '0.5rem'
                    }}>
                      🌐 Protocol Breakdown
                    </h3>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', fontSize: '0.875rem' }}>
                        <thead>
                          <tr style={{ background: '#f1f5f9', borderBottom: '2px solid #cbd5e1' }}>
                            <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Protocol</th>
                            <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600' }}>Peak</th>
                            <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600' }}>Packets</th>
                            <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600' }}>%</th>
                          </tr>
                        </thead>
                        <tbody>
                          {kentikAnalysis.protocols?.results?.[0]?.data?.map((proto: any, index: number) => {
                            const totalPackets = kentikAnalysis.protocols.results[0].data.reduce((sum: number, p: any) => sum + (p.sum_packets || 0), 0);
                            return (
                              <tr key={index} style={{
                                borderBottom: '1px solid #e2e8f0',
                                background: index % 2 === 0 ? 'white' : '#f8fafc'
                              }}>
                                <td style={{ padding: '0.75rem', fontWeight: 'bold', color: '#0f172a' }}>
                                  {proto.Proto || proto.key || 'N/A'}
                                </td>
                                <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold', color: '#dc2626' }}>
                                  {formatGbps(proto.max_bits_per_sec || 0)}
                                </td>
                                <td style={{ padding: '0.75rem', textAlign: 'right', color: '#64748b' }}>
                                  {formatNumber(proto.sum_packets || 0)}
                                </td>
                                <td style={{ padding: '0.75rem', textAlign: 'right', color: '#3b82f6', fontWeight: 'bold' }}>
                                  {formatPercent(proto.sum_packets || 0, totalPackets)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      {(!kentikAnalysis.protocols?.results?.[0]?.data || kentikAnalysis.protocols.results[0].data.length === 0) && (
                        <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>
                          ⚠️ Nenhum dado encontrado
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Section 5: Packet Size Distribution */}
                  <div style={{
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '1.5rem'
                  }}>
                    <h3 style={{
                      margin: '0 0 1rem 0',
                      fontSize: '1.125rem',
                      fontWeight: 'bold',
                      color: '#1e293b',
                      borderBottom: '2px solid #8b5cf6',
                      paddingBottom: '0.5rem'
                    }}>
                      📦 Packet Size Distribution
                    </h3>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', fontSize: '0.875rem' }}>
                        <thead>
                          <tr style={{ background: '#f1f5f9', borderBottom: '2px solid #cbd5e1' }}>
                            <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Avg Size</th>
                            <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600' }}>Peak</th>
                            <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600' }}>Packets</th>
                          </tr>
                        </thead>
                        <tbody>
                          {kentikAnalysis.packetSizes?.results?.[0]?.data?.slice(0, 10).map((size: any, index: number) => (
                            <tr key={index} style={{
                              borderBottom: '1px solid #e2e8f0',
                              background: index % 2 === 0 ? 'white' : '#f8fafc'
                            }}>
                              <td style={{ padding: '0.75rem', fontFamily: 'monospace', color: '#0f172a', fontWeight: 'bold' }}>
                                {size.PktSize || size.key || 'N/A'} bytes
                              </td>
                              <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold', color: '#dc2626' }}>
                                {formatGbps(size.max_bits_per_sec || 0)}
                              </td>
                              <td style={{ padding: '0.75rem', textAlign: 'right', color: '#64748b' }}>
                                {formatNumber(size.sum_packets || 0)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {(!kentikAnalysis.packetSizes?.results?.[0]?.data || kentikAnalysis.packetSizes.results[0].data.length === 0) && (
                        <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>
                          ⚠️ Nenhum dado encontrado
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Section 6: TCP Flags (only if TCP detected) */}
                  {kentikAnalysis.tcpFlags?.results?.[0]?.data && kentikAnalysis.tcpFlags.results[0].data.length > 0 && (
                    <div style={{
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      padding: '1.5rem'
                    }}>
                      <h3 style={{
                        margin: '0 0 1rem 0',
                        fontSize: '1.125rem',
                        fontWeight: 'bold',
                        color: '#1e293b',
                        borderBottom: '2px solid #ef4444',
                        paddingBottom: '0.5rem'
                      }}>
                        🚩 TCP Flags Analysis
                      </h3>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', fontSize: '0.875rem' }}>
                          <thead>
                            <tr style={{ background: '#f1f5f9', borderBottom: '2px solid #cbd5e1' }}>
                              <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Flags</th>
                              <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600' }}>Peak</th>
                              <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600' }}>Packets</th>
                              <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600' }}>%</th>
                            </tr>
                          </thead>
                          <tbody>
                            {kentikAnalysis.tcpFlags.results[0].data.slice(0, 10).map((flag: any, index: number) => {
                              const totalPackets = kentikAnalysis.tcpFlags.results[0].data.reduce((sum: number, f: any) => sum + (f.sum_packets || 0), 0);
                              return (
                                <tr key={index} style={{
                                  borderBottom: '1px solid #e2e8f0',
                                  background: index % 2 === 0 ? 'white' : '#f8fafc'
                                }}>
                                  <td style={{ padding: '0.75rem', fontFamily: 'monospace', fontWeight: 'bold', color: '#dc2626' }}>
                                    {flag.TCP_flags || flag.key || 'N/A'}
                                  </td>
                                  <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold', color: '#dc2626' }}>
                                    {formatGbps(flag.max_bits_per_sec || 0)}
                                  </td>
                                  <td style={{ padding: '0.75rem', textAlign: 'right', color: '#64748b' }}>
                                    {formatNumber(flag.sum_packets || 0)}
                                  </td>
                                  <td style={{ padding: '0.75rem', textAlign: 'right', color: '#3b82f6', fontWeight: 'bold' }}>
                                    {formatPercent(flag.sum_packets || 0, totalPackets)}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                </div>
              )}

              {/* Summary Stats */}
              {!kentikLoading && kentikAnalysis && (
                <div style={{
                  marginTop: '1.5rem',
                  padding: '1.5rem',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: '8px',
                  color: 'white',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '1rem'
                }}>
                  <div>
                    <div style={{ fontSize: '0.75rem', opacity: 0.9, marginBottom: '0.25rem' }}>Total Attackers</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                      {kentikAnalysis.sourceIPs?.results?.[0]?.data?.length || 0}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', opacity: 0.9, marginBottom: '0.25rem' }}>Src Ports</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                      {kentikAnalysis.sourcePorts?.results?.[0]?.data?.length || 0}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', opacity: 0.9, marginBottom: '0.25rem' }}>Dst Ports</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                      {kentikAnalysis.destPorts?.results?.[0]?.data?.length || 0}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', opacity: 0.9, marginBottom: '0.25rem' }}>Protocols</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                      {kentikAnalysis.protocols?.results?.[0]?.data?.length || 0}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', opacity: 0.9, marginBottom: '0.25rem' }}>Pkt Sizes</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                      {kentikAnalysis.packetSizes?.results?.[0]?.data?.length || 0}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', opacity: 0.9, marginBottom: '0.25rem' }}>TCP Flags</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                      {kentikAnalysis.tcpFlags?.results?.[0]?.data?.length || 0}
                    </div>
                  </div>
                </div>
              )}

              {/* AI Analysis Section */}
              {kentikAIAnalysis && (
                <div style={{
                  marginTop: '1.5rem',
                  background: 'linear-gradient(135deg, #10b98115 0%, #05966915 100%)',
                  border: '2px solid #10b981',
                  borderRadius: '12px',
                  padding: '1.5rem'
                }}>
                  <h3 style={{
                    margin: '0 0 1rem 0',
                    fontSize: '1.25rem',
                    fontWeight: 'bold',
                    color: '#065f46',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    🤖 Análise Especializada com IA
                    <span style={{
                      fontSize: '0.75rem',
                      background: '#10b981',
                      color: 'white',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px'
                    }}>
                      Gemini AI
                    </span>
                  </h3>

                  {/* Executive Summary */}
                  <div style={{
                    background: 'white',
                    border: '1px solid #d1fae5',
                    borderRadius: '8px',
                    padding: '1rem',
                    marginBottom: '1rem'
                  }}>
                    <div style={{ fontSize: '0.75rem', color: '#059669', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                      📝 RESUMO EXECUTIVO
                    </div>
                    <p style={{ margin: 0, color: '#1e293b', fontSize: '0.95rem', lineHeight: '1.6' }}>
                      {kentikAIAnalysis.executiveSummary}
                    </p>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                    {/* Attack Identification */}
                    <div style={{
                      background: 'white',
                      border: '1px solid #d1fae5',
                      borderRadius: '8px',
                      padding: '1rem'
                    }}>
                      <div style={{ fontSize: '0.75rem', color: '#059669', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                        🎯 TIPO DE ATAQUE
                      </div>
                      <div style={{ fontSize: '1.125rem', fontWeight: 'bold', color: '#0f172a', marginBottom: '0.5rem' }}>
                        {kentikAIAnalysis.attackIdentification?.attackType}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                        Confiança: <span style={{ fontWeight: 'bold', color: '#10b981' }}>
                          {kentikAIAnalysis.attackIdentification?.confidence}%
                        </span>
                      </div>
                    </div>

                    {/* Threat Actor */}
                    <div style={{
                      background: 'white',
                      border: '1px solid #d1fae5',
                      borderRadius: '8px',
                      padding: '1rem'
                    }}>
                      <div style={{ fontSize: '0.75rem', color: '#059669', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                        🦠 AMEAÇA IDENTIFICADA
                      </div>
                      <div style={{ fontSize: '1.125rem', fontWeight: 'bold', color: '#0f172a', marginBottom: '0.25rem' }}>
                        {kentikAIAnalysis.threatActor?.botnetFamily}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                        Nível: <span style={{ fontWeight: 'bold' }}>
                          {kentikAIAnalysis.threatActor?.sophisticationLevel}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Recommendations */}
                  {kentikAIAnalysis.recommendations && kentikAIAnalysis.recommendations.length > 0 && (
                    <div style={{ marginTop: '1rem' }}>
                      <div style={{ fontSize: '0.875rem', color: '#059669', fontWeight: 'bold', marginBottom: '0.75rem' }}>
                        💡 RECOMENDAÇÕES PRIORITÁRIAS
                      </div>
                      {kentikAIAnalysis.recommendations.slice(0, 3).map((rec: any, index: number) => (
                        <div key={index} style={{
                          background: 'white',
                          border: `2px solid ${rec.priority === 'alta' ? '#ef4444' : rec.priority === 'média' ? '#f59e0b' : '#10b981'}`,
                          borderRadius: '6px',
                          padding: '0.75rem',
                          marginBottom: '0.5rem'
                        }}>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '0.25rem'
                          }}>
                            <strong style={{ color: '#0f172a', fontSize: '0.9rem' }}>{rec.action}</strong>
                            <span style={{
                              fontSize: '0.7rem',
                              background: rec.priority === 'alta' ? '#ef4444' : rec.priority === 'média' ? '#f59e0b' : '#10b981',
                              color: 'white',
                              padding: '0.125rem 0.5rem',
                              borderRadius: '3px',
                              fontWeight: 'bold'
                            }}>
                              {rec.priority?.toUpperCase()}
                            </span>
                          </div>
                          <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{rec.rationale}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div style={{
                    marginTop: '1rem',
                    fontSize: '0.75rem',
                    color: '#059669',
                    textAlign: 'center'
                  }}>
                    ✨ Análise gerada por IA • Confiança geral: {kentikAIAnalysis.confidence}%
                  </div>
                </div>
              )}

              <p style={{
                marginTop: '1.5rem',
                textAlign: 'center',
                fontSize: '0.875rem',
                color: '#64748b'
              }}>
                📊 Dados analisados pelo Kentik Data Explorer • {kentikAnalysis ? 'Análise completa' : 'Aguardando dados'}
              </p>
            </div>
          </div>
        )}

        {/* Enhanced Attack Investigation Modal (Progressive Disclosure) */}
        {showingDetails && attackDetails && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '2rem',
              maxWidth: '95vw',
              maxHeight: '95vh',
              overflowY: 'auto',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '2rem',
                borderBottom: '2px solid var(--color-border)',
                paddingBottom: '1rem'
              }}>
                <h2 style={{
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  color: 'var(--color-text-primary)',
                  margin: 0
                }}>
                  {attackDetails.isForensicAnalysis ? '🔬 ANÁLISE FORENSE AVANÇADA' : '🔍 INVESTIGAÇÃO BÁSICA'}
                </h2>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={() => queryKentikData(attackDetails)}
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      border: 'none',
                      padding: '0.5rem 1rem',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                    title="Abrir análise no Kentik Data Explorer"
                  >
                    📊 View in Kentik
                  </button>
                  <button
                    onClick={() => setShowingDetails(false)}
                    style={{
                      background: '#dc2626',
                      color: 'white',
                      border: 'none',
                      padding: '0.5rem 1rem',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    ✕ Fechar
                  </button>
                </div>
              </div>

              {/* FORENSIC ANALYSIS VIEW */}
              {attackDetails.isForensicAnalysis ? (
                <div>
                  {/* Executive Summary */}
                  <div style={{
                    background: attackDetails.summary?.severity === 'CRITICAL' ? '#fef2f2' :
                               attackDetails.summary?.severity === 'HIGH' ? '#fffbeb' : '#f0fdf4',
                    border: attackDetails.summary?.severity === 'CRITICAL' ? '2px solid #dc2626' :
                            attackDetails.summary?.severity === 'HIGH' ? '2px solid #f59e0b' : '2px solid #16a34a',
                    borderRadius: '8px',
                    padding: '1.5rem',
                    marginBottom: '2rem'
                  }}>
                    <h3 style={{
                      fontSize: '1.25rem',
                      fontWeight: 'bold',
                      color: 'var(--color-text-primary)',
                      marginBottom: '1rem'
                    }}>
                      📊 RESUMO FORENSE
                    </h3>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: '1rem'
                    }}>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>Severidade</div>
                        <div style={{ fontWeight: 'bold', color: 'var(--color-critical)' }}>
                          {attackDetails.summary?.severity}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>Threat Level</div>
                        <div style={{ fontWeight: 'bold', color: 'var(--color-warning)' }}>
                          {attackDetails.summary?.threatLevel}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>Forensic Confidence</div>
                        <div style={{ fontWeight: 'bold', color: 'var(--color-success)' }}>
                          {attackDetails.summary?.forensicConfidence}%
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Botnet Analysis */}
                  {attackDetails.botnetAnalysis && (
                    <div style={{ marginBottom: '2rem' }}>
                      <h4 style={{ fontSize: '1rem', fontWeight: 'bold', color: 'var(--color-text-primary)', marginBottom: '1rem' }}>
                        🤖 BOTNET FINGERPRINTING
                      </h4>
                      <div style={{
                        background: 'rgba(220, 38, 38, 0.1)',
                        border: '1px solid rgba(220, 38, 38, 0.3)',
                        borderRadius: '6px',
                        padding: '1rem'
                      }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                          <div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>Primary Botnet</div>
                            <div style={{ fontWeight: 'bold', color: 'var(--color-critical)' }}>
                              {attackDetails.botnetAnalysis.primaryBotnet.name.toUpperCase()} ({attackDetails.botnetAnalysis.primaryBotnet.confidence}%)
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>Coordination Level</div>
                            <div style={{ fontWeight: 'bold' }}>
                              {attackDetails.botnetAnalysis.infrastructureAnalysis.coordinationLevel}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>Estimated Botnet Size</div>
                            <div style={{ fontWeight: 'bold' }}>
                              {attackDetails.botnetAnalysis.infrastructureAnalysis.estimatedBotnetSize.toLocaleString()} devices
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>C&C Infrastructure</div>
                            <div style={{ fontWeight: 'bold' }}>
                              {attackDetails.botnetAnalysis.infrastructureAnalysis.c2Servers.estimatedC2Servers} servers
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Packet Analysis */}
                  {attackDetails.packetAnalysis && (
                    <div style={{ marginBottom: '2rem' }}>
                      <h4 style={{ fontSize: '1rem', fontWeight: 'bold', color: 'var(--color-text-primary)', marginBottom: '1rem' }}>
                        📦 ANÁLISE DE PACOTES
                      </h4>
                      <div style={{
                        background: 'rgba(59, 130, 246, 0.1)',
                        border: '1px solid #bfdbfe',
                        borderRadius: '6px',
                        padding: '1rem'
                      }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                          <div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>Packet Size Pattern</div>
                            <div style={{ fontWeight: 'bold' }}>
                              {attackDetails.packetAnalysis.sizeDistribution.detectedRange}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>TCP Flags</div>
                            <div style={{ fontWeight: 'bold' }}>
                              {attackDetails.packetAnalysis.protocolCharacteristics.tcpFlags.join('+')}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>Estimated PPS</div>
                            <div style={{ fontWeight: 'bold', color: 'var(--color-critical)' }}>
                              {attackDetails.packetAnalysis.flowAnalysis.estimatedPacketsPerSecond.toLocaleString()}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>Attack Pattern</div>
                            <div style={{ fontWeight: 'bold' }}>
                              {attackDetails.packetAnalysis.sizeDistribution.pattern}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>Burst Classification</div>
                            <div style={{ fontWeight: 'bold' }}>
                              {attackDetails.packetAnalysis.flowAnalysis.burstPattern}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>Attack Signature</div>
                            <div style={{ fontWeight: 'bold', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {attackDetails.packetAnalysis.attackFingerprint.signature}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Campaign Analysis */}
                  {attackDetails.campaignAnalysis && (
                    <div style={{ marginBottom: '2rem' }}>
                      <h4 style={{ fontSize: '1rem', fontWeight: 'bold', color: 'var(--color-text-primary)', marginBottom: '1rem' }}>
                        🎯 CAMPAIGN CORRELATION
                      </h4>
                      <div style={{
                        background: 'rgba(245, 158, 11, 0.1)',
                        border: '1px solid #fed7aa',
                        borderRadius: '6px',
                        padding: '1rem'
                      }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                          <div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>Campaign Name</div>
                            <div style={{ fontWeight: 'bold', color: 'var(--color-warning)' }}>
                              {attackDetails.campaignAnalysis.campaignName}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>Coordination Level</div>
                            <div style={{ fontWeight: 'bold' }}>
                              {attackDetails.campaignAnalysis.coordinationLevel}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>Related Attacks</div>
                            <div style={{ fontWeight: 'bold' }}>
                              Technical: {attackDetails.campaignAnalysis.campaignIndicators.technical}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>MITRE ATT&CK</div>
                            <div style={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                              {attackDetails.campaignAnalysis.ttps.tactics.join(', ')}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Mitigation Recommendations */}
                  {attackDetails.mitigationRecommendations && (
                    <div style={{ marginBottom: '2rem' }}>
                      <h4 style={{ fontSize: '1rem', fontWeight: 'bold', color: 'var(--color-text-primary)', marginBottom: '1rem' }}>
                        🛡️ RECOMENDAÇÕES ESPECÍFICAS
                      </h4>
                      <div style={{ display: 'grid', gap: '1rem' }}>
                        {attackDetails.mitigationRecommendations.specificRules.map((rule: any, index: number) => (
                          <div key={index} style={{
                            background: 'rgba(16, 185, 129, 0.1)',
                            border: '1px solid #bbf7d0',
                            borderRadius: '6px',
                            padding: '1rem'
                          }}>
                            <div style={{
                              fontSize: '0.875rem',
                              fontWeight: 'bold',
                              color: 'var(--color-success-dark)',
                              marginBottom: '0.5rem'
                            }}>
                              {rule.name} (Efetividade: {rule.effectiveness}%)
                            </div>
                            <div style={{
                              fontSize: '0.75rem',
                              color: 'var(--color-text-secondary)',
                              fontFamily: 'monospace',
                              background: 'white',
                              padding: '0.5rem',
                              borderRadius: '4px',
                              marginBottom: '0.5rem',
                              border: '1px solid var(--color-border)'
                            }}>
                              {rule.flowspecRule}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--color-success-dark)' }}>
                              <strong>Rationale:</strong> {rule.rationale}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--color-success)', marginTop: '0.25rem' }}>
                              <strong>Preserva:</strong> {rule.preserves}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                /* BASIC ANALYSIS VIEW */
                <div>
                  {/* Basic Attack Summary */}
                  <div style={{
                    background: attackDetails.analysis?.severity === 'CRITICAL' ? '#fef2f2' :
                               attackDetails.analysis?.severity === 'HIGH' ? '#fffbeb' : '#f0fdf4',
                    border: attackDetails.analysis?.severity === 'CRITICAL' ? '2px solid #dc2626' :
                            attackDetails.analysis?.severity === 'HIGH' ? '2px solid #f59e0b' : '2px solid #16a34a',
                    borderRadius: '8px',
                    padding: '1.5rem',
                    marginBottom: '2rem'
                  }}>
                    <h3 style={{
                      fontSize: '1.25rem',
                      fontWeight: 'bold',
                      color: 'var(--color-text-primary)',
                      fontFamily: 'monospace',
                      marginBottom: '1rem'
                    }}>
                      {attackDetails.ipAddress}
                    </h3>
                    <p style={{
                      fontSize: '1rem',
                      color: 'var(--color-critical)',
                      fontWeight: 'bold',
                      marginBottom: '1rem'
                    }}>
                      {attackDetails.description} • {attackDetails.analysis?.severity || 'HIGH'}
                    </p>

                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                      gap: '1rem'
                    }}>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>Peak Volume</div>
                        <div style={{ fontWeight: 'bold', color: 'var(--color-critical)' }}>
                          {attackDetails.peakVolume} {attackDetails.units}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>Duração</div>
                        <div style={{ fontWeight: 'bold' }}>{attackDetails.duration}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>Cliente</div>
                        <div style={{ fontWeight: 'bold' }}>{attackDetails.assetGroup || 'Prospect'}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>Status</div>
                        <div style={{
                          fontWeight: 'bold',
                          color: attackDetails.status === 'ongoing' ? '#dc2626' : '#16a34a'
                        }}>
                          {attackDetails.status === 'ongoing' ? '🔴 ATIVO' : '✅ PROTEGIDO'}
                        </div>
                      </div>
                    </div>

                    <div style={{
                      background: 'rgba(59, 130, 246, 0.1)',
                      border: '1px solid #bfdbfe',
                      borderRadius: '6px',
                      padding: '1rem',
                      marginTop: '1rem'
                    }}>
                      <p style={{ fontSize: '0.875rem', color: 'var(--color-primary)', fontWeight: 'bold', margin: 0 }}>
                        💡 Para análise forense detalhada, use o botão "🔬 Análise Forense" na SOC Dashboard
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Recommendations */}
              <div>
                <h4 style={{ fontSize: '1rem', fontWeight: 'bold', color: 'var(--color-text-primary)', marginBottom: '1rem' }}>
                  ⚡ Ações Recomendadas SOFTDADOS
                </h4>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => {
                      setDestination(attackDetails.ipAddress || attackDetails.attackId);
                      setActiveModule('protection');
                      setShowingDetails(false);
                    }}
                    style={{
                      background: '#dc2626',
                      color: 'white',
                      border: 'none',
                      padding: '0.75rem 1rem',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    🛡️ Implementar Proteção
                  </button>
                  <button
                    onClick={() => {
                      const ipToCopy = attackDetails.ipAddress || attackDetails.attackId;
                      navigator.clipboard.writeText(ipToCopy);
                      alert('IP copiado para clipboard!');
                    }}
                    style={{
                      background: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      padding: '0.75rem 1rem',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    📋 Copiar IP
                  </button>
                  {attackDetails.isForensicAnalysis && (
                    <button
                      onClick={() => {
                        const forensicData = JSON.stringify(attackDetails, null, 2);
                        const blob = new Blob([forensicData], { type: 'application/json' });
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `forensic-analysis-${attackDetails.attackId}-${new Date().toISOString().slice(0, 10)}.json`;
                        a.click();
                        window.URL.revokeObjectURL(url);
                      }}
                      style={{
                        background: '#8b5cf6',
                        color: 'white',
                        border: 'none',
                        padding: '0.75rem 1rem',
                        borderRadius: '6px',
                        fontSize: '0.875rem',
                        fontWeight: 'bold',
                        cursor: 'pointer'
                      }}
                    >
                      📄 Export Forensics
                    </button>
                  )}
                  <button
                    onClick={() => window.open(`https://ddos.softdados.net`, '_blank')}
                    style={{
                      background: '#10b981',
                      color: 'white',
                      border: 'none',
                      padding: '0.75rem 1rem',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    📊 Ver Painel Corero
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Preview Modal (RESTORED) */}
        {showingPreview && previewData && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '2rem',
              maxWidth: '900px',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1.5rem'
              }}>
                <h3 style={{
                  fontSize: '1.25rem',
                  fontWeight: 'bold',
                  color: 'var(--color-text-primary)',
                  margin: 0
                }}>
                  👁️ PREVIEW DAS REGRAS - APROVAÇÃO SOC
                </h3>
                <button
                  onClick={() => {
                    setShowingPreview(false);
                    setPreviewData(null);
                  }}
                  style={{
                    background: 'transparent',
                    color: 'var(--color-text-secondary)',
                    border: '1px solid #d1d5db',
                    padding: '0.5rem 1rem',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    cursor: 'pointer'
                  }}
                >
                  ✕ Fechar Preview
                </button>
              </div>

              <div style={{
                background: 'rgba(59, 130, 246, 0.1)',
                border: '1px solid #bfdbfe',
                borderRadius: '6px',
                padding: '1rem',
                marginBottom: '1.5rem'
              }}>
                <h4 style={{ fontSize: '1rem', fontWeight: 'bold', color: 'var(--color-primary)', marginBottom: '0.5rem' }}>
                  📊 RESUMO DA PROTEÇÃO
                </h4>
                <div style={{ fontSize: '0.875rem', color: 'var(--color-text-primary)' }}>
                  <p><strong>Total de regras:</strong> {previewData.totalRules}</p>
                  <p><strong>Subredes /24:</strong> {previewData.rulesByDestination.length}</p>
                </div>
              </div>

              {/* Approval Actions (RESTORED) */}
              <div style={{
                background: 'rgba(16, 185, 129, 0.1)',
                border: '1px solid #bbf7d0',
                borderRadius: '6px',
                padding: '1rem'
              }}>
                <h4 style={{
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  color: 'var(--color-success-dark)',
                  marginBottom: '0.75rem'
                }}>
                  ⚖️ APROVAÇÃO SOC
                </h4>
                <p style={{
                  fontSize: '0.875rem',
                  color: 'var(--color-success-dark)',
                  marginBottom: '1rem'
                }}>
                  Revisar as {previewData.totalRules} regras de proteção. Aprovar apenas se satisfeito com a proteção proposta.
                </p>
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <button
                    onClick={async () => {
                      if (window.confirm(`✅ APROVAR PROTEÇÃO SOFTDADOS\n\nVocê está prestes a criar ${previewData.totalRules} regras FlowSpec.\n\nEssas regras direcionarão tráfego suspeito para o Scrubbing Center.\n\nContinuar?`)) {
                        setShowingPreview(false);
                        await createRules();
                      }
                    }}
                    style={{
                      background: '#16a34a',
                      color: 'white',
                      padding: '0.75rem 1.5rem',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '1rem',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    ✅ APROVAR E IMPLEMENTAR ({previewData.totalRules} regras)
                  </button>
                  <button
                    onClick={() => {
                      setShowingPreview(false);
                      setPreviewData(null);
                      alert('🚫 Preview cancelado. Você pode fazer nova análise IA ou ajustar destinos.');
                    }}
                    style={{
                      background: '#dc2626',
                      color: 'white',
                      padding: '0.75rem 1.5rem',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '1rem',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    🚫 REJEITAR
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* FlowSpec/BPF Config Modal */}
        {showingConfigModal && selectedAttackConfigs && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '1rem'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '0',
              maxWidth: '90vw',
              width: '100%',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
            }}>
              {/* Modal Header */}
              <div style={{
                background: 'linear-gradient(90deg, #8b5cf6, #3b82f6)',
                color: 'white',
                padding: '1.5rem 2rem',
                borderRadius: '12px 12px 0 0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <h2 style={{
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    margin: 0,
                    marginBottom: '0.5rem'
                  }}>
                    🤖 Configurações IA - Mitigação DDoS
                  </h2>
                  <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>
                    Ataque: <strong style={{ fontFamily: 'monospace' }}>{selectedAttackConfigs.attack?.ipAddress}</strong> • {selectedAttackConfigs.attack?.description}
                  </div>
                </div>
                <button
                  onClick={() => setShowingConfigModal(false)}
                  style={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    border: 'none',
                    padding: '0.5rem 1rem',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                >
                  ✕ Fechar
                </button>
              </div>

              {/* AI Badge */}
              {selectedAttackConfigs.aiGenerated && (
                <div style={{
                  background: 'linear-gradient(90deg, #8b5cf6, #3b82f6)',
                  color: 'white',
                  padding: '0.75rem',
                  textAlign: 'center',
                  fontWeight: 'bold',
                  fontSize: '0.875rem'
                }}>
                  ✨ Análise gerada por Inteligência Artificial
                </div>
              )}

              {/* Tab Navigation */}
              <div style={{
                display: 'flex',
                borderBottom: '2px solid var(--color-border)',
                background: 'var(--color-bg-elevated)',
                padding: '0 2rem'
              }}>
                <button
                  onClick={() => setConfigModalTab('flowspec')}
                  style={{
                    background: configModalTab === 'flowspec' ? 'white' : 'transparent',
                    color: configModalTab === 'flowspec' ? '#8b5cf6' : '#6b7280',
                    border: 'none',
                    borderBottom: configModalTab === 'flowspec' ? '3px solid #8b5cf6' : 'none',
                    padding: '1rem 1.5rem',
                    fontSize: '0.875rem',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    marginBottom: '-2px'
                  }}
                >
                  🛡️ FlowSpec Edge ({selectedAttackConfigs.flowspecRules?.length || 0})
                </button>
                <button
                  onClick={() => setConfigModalTab('bpf')}
                  style={{
                    background: configModalTab === 'bpf' ? 'white' : 'transparent',
                    color: configModalTab === 'bpf' ? '#3b82f6' : '#6b7280',
                    border: 'none',
                    borderBottom: configModalTab === 'bpf' ? '3px solid #3b82f6' : 'none',
                    padding: '1rem 1.5rem',
                    fontSize: '0.875rem',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    marginBottom: '-2px'
                  }}
                >
                  ⚡ BPF Corero ({selectedAttackConfigs.bpfFilters?.length || 0})
                </button>
                <button
                  onClick={() => setConfigModalTab('infrastructure')}
                  style={{
                    background: configModalTab === 'infrastructure' ? 'white' : 'transparent',
                    color: configModalTab === 'infrastructure' ? '#10b981' : '#6b7280',
                    border: 'none',
                    borderBottom: configModalTab === 'infrastructure' ? '3px solid #10b981' : 'none',
                    padding: '1rem 1.5rem',
                    fontSize: '0.875rem',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    marginBottom: '-2px'
                  }}
                >
                  🔧 Ações Infraestrutura ({selectedAttackConfigs.infrastructureActions?.length || 0})
                </button>
              </div>

              {/* Modal Content */}
              <div style={{
                overflowY: 'auto',
                padding: '2rem',
                flex: 1
              }}>
                {/* FlowSpec Rules Tab */}
                {configModalTab === 'flowspec' && (
                  <div>
                    <div style={{ marginBottom: '1rem' }}>
                      <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--color-info)', marginBottom: '0.5rem' }}>
                        🛡️ Regras BGP FlowSpec para Juniper Edge
                      </h3>
                      <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                        Configurações prontas para implementação no roteador Juniper via ExaBGP
                      </p>
                    </div>

                    {selectedAttackConfigs.flowspecRules?.length > 0 ? (
                      <div style={{ display: 'grid', gap: '1.5rem' }}>
                        {selectedAttackConfigs.flowspecRules.map((rule: any, index: number) => (
                          <div key={index} style={{
                            background: 'rgba(139, 92, 246, 0.1)',
                            border: '2px solid #8b5cf6',
                            borderRadius: '8px',
                            padding: '1.5rem'
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                              <div>
                                <h4 style={{ fontSize: '1rem', fontWeight: 'bold', color: 'var(--color-info)', margin: 0 }}>
                                  {rule.name}
                                </h4>
                                <span style={{
                                  background: rule.priority === 'alta' ? '#dc2626' : rule.priority === 'média' ? '#f59e0b' : '#10b981',
                                  color: 'white',
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '4px',
                                  fontSize: '0.75rem',
                                  fontWeight: 'bold',
                                  marginTop: '0.5rem',
                                  display: 'inline-block'
                                }}>
                                  Prioridade: {rule.priority?.toUpperCase()}
                                </span>
                              </div>
                              <div style={{ textAlign: 'right' }}>
                                <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>Efetividade</div>
                                <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--color-success)' }}>{rule.effectiveness}</div>
                              </div>
                            </div>

                            <div style={{
                              background: '#1f2937',
                              color: '#f9fafb',
                              padding: '1rem',
                              borderRadius: '6px',
                              fontFamily: 'monospace',
                              fontSize: '0.875rem',
                              whiteSpace: 'pre-wrap',
                              marginBottom: '1rem',
                              position: 'relative'
                            }}>
                              {rule.rule}
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(rule.rule);
                                  alert('✅ Regra FlowSpec copiada para a área de transferência!');
                                }}
                                style={{
                                  position: 'absolute',
                                  top: '0.5rem',
                                  right: '0.5rem',
                                  background: '#3b82f6',
                                  color: 'white',
                                  border: 'none',
                                  padding: '0.375rem 0.75rem',
                                  borderRadius: '4px',
                                  fontSize: '0.75rem',
                                  fontWeight: 'bold',
                                  cursor: 'pointer'
                                }}
                              >
                                📋 Copiar
                              </button>
                            </div>

                            <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                              <strong>Justificativa:</strong> {rule.rationale}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-secondary)' }}>
                        Nenhuma regra FlowSpec disponível
                      </div>
                    )}
                  </div>
                )}

                {/* BPF Filters Tab */}
                {configModalTab === 'bpf' && (
                  <div>
                    <div style={{ marginBottom: '1rem' }}>
                      <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--color-primary)', marginBottom: '0.5rem' }}>
                        ⚡ Filtros BPF para Corero NTD
                      </h3>
                      <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                        Filtros customizados para scrubbing local nas caixas Corero
                      </p>
                    </div>

                    {selectedAttackConfigs.bpfFilters?.length > 0 ? (
                      <div style={{ display: 'grid', gap: '1.5rem' }}>
                        {selectedAttackConfigs.bpfFilters.map((filter: any, index: number) => (
                          <div key={index} style={{
                            background: 'rgba(59, 130, 246, 0.1)',
                            border: '2px solid #3b82f6',
                            borderRadius: '8px',
                            padding: '1.5rem'
                          }}>
                            <h4 style={{ fontSize: '1rem', fontWeight: 'bold', color: 'var(--color-primary)', marginBottom: '0.5rem' }}>
                              {filter.name}
                            </h4>
                            <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginBottom: '1rem' }}>
                              {filter.description}
                            </p>

                            <div style={{
                              background: '#1f2937',
                              color: '#f9fafb',
                              padding: '1rem',
                              borderRadius: '6px',
                              fontFamily: 'monospace',
                              fontSize: '0.875rem',
                              whiteSpace: 'pre-wrap',
                              marginBottom: '1rem',
                              position: 'relative'
                            }}>
                              {filter.filter}
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(filter.filter);
                                  alert('✅ Filtro BPF copiado para a área de transferência!');
                                }}
                                style={{
                                  position: 'absolute',
                                  top: '0.5rem',
                                  right: '0.5rem',
                                  background: '#3b82f6',
                                  color: 'white',
                                  border: 'none',
                                  padding: '0.375rem 0.75rem',
                                  borderRadius: '4px',
                                  fontSize: '0.75rem',
                                  fontWeight: 'bold',
                                  cursor: 'pointer'
                                }}
                              >
                                📋 Copiar
                              </button>
                            </div>

                            <div style={{
                              background: 'rgba(16, 185, 129, 0.1)',
                              border: '1px solid #bbf7d0',
                              borderRadius: '4px',
                              padding: '0.75rem',
                              fontSize: '0.875rem',
                              color: 'var(--color-success-dark)'
                            }}>
                              <strong>Caso de Uso:</strong> {filter.useCase}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-secondary)' }}>
                        Nenhum filtro BPF disponível
                      </div>
                    )}
                  </div>
                )}

                {/* Infrastructure Actions Tab */}
                {configModalTab === 'infrastructure' && (
                  <div>
                    <div style={{ marginBottom: '1rem' }}>
                      <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--color-success)', marginBottom: '0.5rem' }}>
                        🔧 Ações de Infraestrutura Recomendadas
                      </h3>
                      <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                        Melhorias e ajustes sugeridos pela IA para otimizar a proteção
                      </p>
                    </div>

                    {selectedAttackConfigs.infrastructureActions?.length > 0 ? (
                      <div style={{ display: 'grid', gap: '1rem' }}>
                        {selectedAttackConfigs.infrastructureActions.map((action: string, index: number) => (
                          <div key={index} style={{
                            background: 'rgba(16, 185, 129, 0.1)',
                            border: '2px solid #10b981',
                            borderRadius: '8px',
                            padding: '1rem',
                            display: 'flex',
                            alignItems: 'start',
                            gap: '1rem'
                          }}>
                            <div style={{
                              background: '#10b981',
                              color: 'white',
                              borderRadius: '50%',
                              width: '30px',
                              height: '30px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 'bold',
                              flexShrink: 0
                            }}>
                              {index + 1}
                            </div>
                            <div style={{ fontSize: '0.875rem', color: 'var(--color-success-dark)', flex: 1 }}>
                              {action}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-secondary)' }}>
                        Nenhuma ação de infraestrutura recomendada
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div style={{
                background: 'var(--color-bg-elevated)',
                borderTop: '2px solid #e5e7eb',
                padding: '1rem 2rem',
                borderRadius: '0 0 12px 12px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
                  Peak: <strong>{selectedAttackConfigs.attack?.peakVolume}</strong> • Duração: <strong>{selectedAttackConfigs.attack?.duration}</strong>
                </div>
                <button
                  onClick={() => {
                    const configs = `# CONFIGURAÇÕES DE MITIGAÇÃO - ${selectedAttackConfigs.attack?.ipAddress}
# Ataque: ${selectedAttackConfigs.attack?.description}
# Peak: ${selectedAttackConfigs.attack?.peakVolume} • Duração: ${selectedAttackConfigs.attack?.duration}
# Gerado por: SOFTDADOS IA

## FlowSpec Edge Rules (Juniper)
${selectedAttackConfigs.flowspecRules?.map((r: any, i: number) => `
### ${i + 1}. ${r.name} (${r.priority}, ${r.effectiveness})
${r.rule}
# ${r.rationale}
`).join('\n') || 'Nenhuma regra FlowSpec'}

## BPF Filters (Corero NTD)
${selectedAttackConfigs.bpfFilters?.map((f: any, i: number) => `
### ${i + 1}. ${f.name}
# ${f.description}
${f.filter}
# Caso de uso: ${f.useCase}
`).join('\n') || 'Nenhum filtro BPF'}

## Ações de Infraestrutura
${selectedAttackConfigs.infrastructureActions?.map((a: string, i: number) => `${i + 1}. ${a}`).join('\n') || 'Nenhuma ação recomendada'}
`;
                    const blob = new Blob([configs], { type: 'text/plain' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `configs-${selectedAttackConfigs.attack?.ipAddress}-${Date.now()}.conf`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  style={{
                    background: 'linear-gradient(90deg, #8b5cf6, #3b82f6)',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                >
                  💾 Exportar Todas as Configs
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div style={{
            background: 'rgba(220, 38, 38, 0.1)',
            border: '1px solid rgba(220, 38, 38, 0.3)',
            color: 'var(--color-critical)',
            padding: '1rem',
            borderRadius: '6px',
            marginTop: '1rem'
          }}>
            <strong>Erro:</strong> {error}
          </div>
        )}

        {/* Footer (RESTORED) */}
        <div style={{
          background: 'rgba(59, 130, 246, 0.1)',
          border: '1px solid #0ea5e9',
          color: 'var(--color-primary)',
          padding: '1.5rem',
          borderRadius: '8px',
          fontSize: '0.875rem',
          marginTop: '2rem'
        }}>
          <h4 style={{ fontWeight: 'bold', marginBottom: '1rem', fontSize: '1rem' }}>
            ℹ️ SOFTDADOS SOC CORERO MANAGER - Provedor de Proteção DDoS
          </h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
            <div>
              <h5 style={{ fontWeight: 'bold', color: 'var(--color-critical)', marginBottom: '0.5rem' }}>🚨 SOC Dashboard</h5>
              <p style={{ fontSize: '0.8rem', lineHeight: '1.4' }}>
                Centro de operações com {attackData?.ongoing?.length || 0} ataques ativos,
                análise de ameaças e resposta a incidentes para clientes.
              </p>
            </div>
            <div>
              <h5 style={{ fontWeight: 'bold', color: 'var(--color-success)', marginBottom: '0.5rem' }}>🛡️ Protection Manager</h5>
              <p style={{ fontSize: '0.8rem', lineHeight: '1.4' }}>
                Proteção DDoS inteligente com IA, {monitoringData?.flowspecStats?.activeRules || 0} regras ativas,
                batch operations e direcionamento para scrubbing.
              </p>
            </div>
            <div>
              <h5 style={{ fontWeight: 'bold', color: 'var(--color-info)', marginBottom: '0.5rem' }}>📊 Monitoring</h5>
              <p style={{ fontSize: '0.8rem', lineHeight: '1.4' }}>
                Métricas em tempo real da plataforma SOFTDADOS,
                performance de delivery e SLA para clientes.
              </p>
            </div>
            <div>
              <h5 style={{ fontWeight: 'bold', color: 'var(--color-critical)', marginBottom: '0.5rem' }}>📈 Reports</h5>
              <p style={{ fontSize: '0.8rem', lineHeight: '1.4' }}>
                Relatórios multi-tenant para customer success,
                306 prospects identificados e business intelligence.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

// Main App component with authentication
function App() {
  return (
    <AuthProvider>
      <AuthGate />
    </AuthProvider>
  );
}

// Authentication gate component
function AuthGate() {
  const { isAuthenticated, login, loading } = useAuth();

  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'var(--color-bg-primary)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <div style={{
          fontSize: '64px',
          marginBottom: '20px'
        }}>🛡️</div>
        <div style={{
          width: '48px',
          height: '48px',
          border: '4px solid rgba(14, 165, 233, 0.2)',
          borderTop: '4px solid var(--color-primary)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <div style={{
          color: 'var(--color-text-secondary)',
          fontSize: '16px'
        }}>
          Carregando SOFTDADOS SOC...
        </div>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login onLoginSuccess={login} />;
  }

  return <AppContent />;
}

export default App;